// Import React hooks and components for the AI Generation Wizard component
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatCredits } from '../../../utils/numberFormat';
import { useNavigate, useLocation } from 'react-router-dom';
import useRouteMeta from '../../../hooks/useRouteMeta';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover
} from '@mui/material';
import {
  CloudUpload,
  Close,
  AutoAwesome,
  Download,
  Feedback,
  Star,
  StarBorder,
  Person,
  Accessibility,
  Crop,
  Image as ImageIcon,
  Videocam,
  Gif,
  Psychology,
  Memory,
  SmartDisplay,
  Bolt,
  Palette,
  Brush,
  Architecture,
  AutoFixHigh,
  ColorLens,
  Inventory2,
  InsertDriveFile,
  Warning,
  Add,
  CheckCircle,
  Schedule,
  Error,
  PlayArrow,
  Cancel,
  RateReview,
  Refresh,
  Delete,
  Search,
  FilterList,
  Info,
  Help,
  History,
  ArrowForward,
  ArrowUpward,
  Check,
  KeyboardArrowDown
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import GenerationPreviewDialog from '../History/GenerationPreviewDialog';
import { aiGenerationAPI, creditsAPI, merchantAuthAPI, merchantModelFaceAPI } from '../../../services/merchantAPI';
import FlutterServicesAPI from '../../../services/flutterServices';
import MerchantWelcomeSection from '../Layout/MerchantWelcomeSection';
import { API_URLS } from '../../../config/apiConfig';
import { getModelFaceThumbnailUrl as getSecureThumbnailUrl } from '../../../utils/imageUrlUtils';
import SecureImage from '../../Common/SecureImage';
import { AutoCloseAlert } from '../../Common';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import DynamicMerchantTabs from '../Layout/DynamicMerchantTabs';
import GlammifyAnimationLoader from './GlammifyAnimationLoader';

// In-memory draft store used only for "Go to AI Model Studio" round-trip.
// Keep it on window to survive component unmount/remount across route changes.
const getModelStudioDraft = () =>
  (typeof window !== 'undefined' ? window.__aiGenerationModelStudioDraft || null : null);
const setModelStudioDraft = (draft) => {
  if (typeof window !== 'undefined') {
    window.__aiGenerationModelStudioDraft = draft;
  }
};
const AI_MODEL_STUDIO_RETURN_KEY = 'ai_generation_model_studio_return';
const clearModelStudioReturnDraft = () => {
  try { localStorage.removeItem(AI_MODEL_STUDIO_RETURN_KEY); } catch (_) { }
  setModelStudioDraft(null);
};

/** Shared invalid / sensitive preview state for product and customer upload tiles. */
function UploadPreviewErrorBox({ isSafetyBlock, error }) {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#FFF5F5',
        border: '1.5px solid #FEB2B2',
        borderRadius: '12px',
        p: 1.5,
        textAlign: 'center',
        boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
      }}
    >
      <Box
        sx={{
          backgroundColor: '#FED7D7',
          borderRadius: '50%',
          p: 0.8,
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Warning sx={{ fontSize: 20, color: '#C53030' }} />
      </Box>
      <Typography
        sx={{
          fontSize: '0.7rem',
          fontWeight: 700,
          color: '#C53030',
          fontFamily: '"Montserrat", sans-serif',
          lineHeight: 1.2,
          textTransform: 'uppercase',
          letterSpacing: '0.02em',
        }}
      >
        {isSafetyBlock ? 'Sensitive Content' : 'Invalid Image'}
      </Typography>
      {error && !isSafetyBlock && (
        <Typography
          sx={{
            fontSize: '0.6rem',
            color: '#742A2A',
            mt: 0.5,
            fontFamily: '"Montserrat", sans-serif',
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
}

/** GIF and IMAGE use media_qualities with applies_to=IMAGE; VIDEO uses applies_to=VIDEO. */
function mediaQualityAppliesScope(outputFormatName) {
  if (!outputFormatName) return 'IMAGE';
  return String(outputFormatName).toUpperCase() === 'VIDEO' ? 'VIDEO' : 'IMAGE';
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});


const UploadArea = styled(Box, {
  shouldForwardProp: (prop) => !['dragActive', 'hasImage', 'hasError'].includes(prop),
})(({ theme, dragActive, hasImage, hasError }) => ({
  border: 'none',
  borderRadius: '17.26px',
  padding: hasImage ? 0 : { xs: '10px', sm: theme.spacing(6) },
  textAlign: 'center',
  backgroundColor: hasImage
    ? 'transparent'
    : dragActive
      ? '#f8fafc'
      : '#ffffff',
  transition: 'all 0.3s ease',
  minHeight: { xs: '180px', sm: '300px' },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  cursor: hasImage ? 'default' : 'pointer',
  boxShadow: '0px 4.32px 12.95px 0px rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.up('sm')]: {
    boxShadow: 'none',
  },
}));

const ImagePreview = styled(Box)({
  position: 'relative',
  width: '100%',
  minHeight: { xs: '180px', sm: '300px' },
  borderRadius: '12px',
  overflow: 'hidden',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  padding: '0px',
  justifyContent: 'center',
  alignItems: 'center',
  '& .image-item': {
    position: 'relative',
    width: 'calc(50% - 4px)',
    height: { xs: '140px', sm: '180px' },
    borderRadius: '8px',
    overflow: 'hidden',
    border: '2px solid #e5e7eb',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: '#6318C6',
      transform: 'scale(1.02)',
      '& .change-image-overlay': {
        opacity: 1,
        visibility: 'visible'
      }
    },
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain'
    },
    '& .change-image-overlay': {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0,
      visibility: 'hidden',
      transition: 'all 0.3s ease',
      zIndex: 2
    },
    '&.single-image': {
      width: '100%',
      height: { xs: '160px', sm: '200px' },
      maxWidth: '400px'
    },
    '&.invalid-image': {
      borderColor: '#ef4444',
      backgroundColor: '#fef2f2',
      '&:hover': {
        borderColor: '#dc2626'
      }
    },
    '&.loading-image': {
      borderColor: '#0ea5e9',
      backgroundColor: '#f0f9ff',
      '&:hover': {
        borderColor: '#0284c7'
      }
    }
  }
});

const RemoveButton = styled(IconButton)({
  position: 'absolute',
  top: '4px',
  right: '4px',
  // Keep remove button above hover overlay so clicks always work.
  zIndex: 3,
  backgroundColor: 'rgba(239, 68, 68, 0.9)',
  color: 'white',
  width: '24px',
  height: '24px',
  '&:hover': {
    backgroundColor: 'rgba(239, 68, 68, 1)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '16px'
  }
});

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: '500',
  padding: '12px 24px',
  fontSize: '16px',
  fontFamily: 'var(--font-montserrat-medium) !important'
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: 'var(--font-montserrat-medium) !important'
  },
  '& .MuiSelect-root': {
    borderRadius: '8px',
    height: '45px',
    minHeight: '45px',
    padding: '0 16px', // Horizontal padding only
    fontWeight: '500',
    fontSize: '15px',
    color: 'black',
    fontFamily: 'var(--font-montserrat-medium) !important',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      fontSize: '14px'
    },
    '& .MuiSelect-select': {
      padding: '0', // Remove all padding from select
      display: 'flex',
      alignItems: 'center',
      fontWeight: '500',
      fontSize: '15px',
      color: 'black',
      height: '100%',
      fontFamily: 'var(--font-montserrat-medium) !important',
      [theme.breakpoints.down('md')]: {
        fontSize: '14px'
      }
    }
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    height: '45px !important',
    minHeight: '45px',
    '& fieldset': {
      borderColor: '#d1d5db'
    },
    '&:hover fieldset': {
      borderColor: '#9ca3af'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#6318C6',
      borderWidth: '2px',
      borderRadius: '8px'
    },
    '&.Mui-focused': {
      backgroundColor: '#f3f0ff'
    },
    '& .MuiSelect-select': {
      padding: '0 16px',
      height: '45px',
      display: 'flex',
      alignItems: 'center'
    }
  },
  '& .MuiMenuItem-root': {
    fontSize: '15px',
    fontWeight: '500',
    color: 'black',
    fontFamily: 'var(--font-montserrat-medium) !important',
    [theme.breakpoints.down('md')]: {
      fontSize: '14px'
    }
  }
}));

const QualityButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  borderRadius: '15px',
  textTransform: 'none',
  fontWeight: '500',
  padding: '10px 25px',
  fontSize: '14px',
  backgroundColor: selected ? '#6318C6' : 'white',
  color: selected ? 'white' : '#1F2937',
  fontFamily: 'var(--font-montserrat-medium) !important',
  border: selected ? 'none' : '1px solid #E5E7EB',
  boxShadow: selected ? '0px 4px 10px rgba(99, 24, 198, 0.2)' : 'none',
  minWidth: '85px',
  '&:hover': {
    backgroundColor: selected ? '#5214a6' : '#f9fafb',
    borderColor: selected ? 'none' : '#D1D5DB',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    padding: '8px 20px',
    borderRadius: '12px',
    minWidth: '70px',
  },
}));

const TagsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  padding: '8px 16px!important',
  border: '1px solid #E5E7EB',
  borderRadius: '8px',
  backgroundColor: 'white',
  height: 'auto',
  minHeight: '45px',
  alignItems: 'center',
  transition: 'all 0.3s ease',
  boxSizing: 'border-box',
  '&:focus-within': {
    borderColor: '#6318C6',
    boxShadow: '0 0 0 1px #6318C6'
  }
}));

const TagInput = styled('input')({
  height: 'auto !important',
  border: 'none',
  outline: 'none',
  fontSize: '15px',
  fontWeight: '500',
  color: 'black',
  flex: 1,
  minWidth: '120px',
  background: 'transparent',
  padding: '0px 0px!important',
  fontFamily: 'var(--font-montserrat-medium) !important',
  '&::placeholder': {
    color: '#9CA3AF',
    fontWeight: '500'
  }
});

const StyledTagChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'white',
  border: '1px solid #000000',
  color: '#000000',
  fontWeight: '500',
  height: '32px',
  borderRadius: '16px',
  fontFamily: 'var(--font-montserrat-medium) !important',
  '& .MuiChip-label': {
    paddingLeft: '12px',
    paddingRight: '8px',
  },
  '& .MuiChip-deleteIcon': {
    color: '#000000',
    fontSize: '18px',
    marginRight: '4px',
    '&:hover': {
      color: '#4B5563'
    }
  }
}));




const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '25px', // Default for desktop
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    fontSize: '17.23px', // Tablet
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '17.23px', // Mobile
  },
}));

const aiModels = [
  { id: '1', name: 'Shanaya', image: '/assets/img/icons/img-1.svg' },
  { id: '2', name: 'Snehal', image: '/assets/img/icons/img-2.svg' },
  { id: '3', name: 'Julie', image: '/assets/img/icons/img-3.svg' }
];

// Match master category `name` or `display_name` (lowercase trim); hidden from grid when Customer Photo tab is selected.
const JEWELLERY_TYPES_HIDDEN_FOR_CUSTOMER_TAB = new Set([
  'bangle',
  'bangles',
  'chain',
  'ring'

]);


const isCategoryHiddenForCustomerPhotoTab = (category) => {
  if (!category) return false;
  const slug = (category.name || category.display_name || '').toLowerCase().trim();
  return JEWELLERY_TYPES_HIDDEN_FOR_CUSTOMER_TAB.has(slug);
};

// Customer-photo tab hides model type UI but the generate API must still send Human Model (not Human Hand / Bust).
const findHumanModelTypeId = (modelTypes) => {
  if (!modelTypes?.length) return null;
  const match = modelTypes.find((mt) => {
    const label = `${mt.name || ''} ${mt.display_name || ''}`.toLowerCase().replace(/[_-]/g, ' ');
    return label.includes('human model') && !label.includes('human hand');
  });
  return match ? String(match.id) : null;
};

/**
 * AI Generation Wizard Component
 * 
 * Features:
 * - Automatic black velvet background selection for human models
 * - Configurable default backgrounds (can be extended with dropdown selection)
 * - File upload with drag & drop support
 * - Dynamic credit calculation
 * - Real-time generation status polling
 */
const AIGenerationWizard = () => {
  const [error, setError] = useState('');
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = useState(true);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);

  // Check auth visibility on mount
  useEffect(() => {
    const checkAuthVisibility = async () => {
      try {
        const response = await merchantAuthAPI.checkAuthVisibility();
        if (response.data && typeof response.data.show_whatsapp_sms_auth === 'boolean') {
          setShowWhatsAppSmsAuth(response.data.show_whatsapp_sms_auth);
        }
      } catch (error) {
        console.error('❌ AIGenerationWizard: Failed to check auth visibility:', error);
      }
    };
    checkAuthVisibility();
  }, []);
  useRouteMeta();
  // Same pattern as Gallery.js for assigned catalog models (JWT + /model-face/thumbnail).
  const getThumbnailUrl = (item, size = 'thumbnail') => {
    if (!item) return '';
    return getSecureThumbnailUrl(item.id, size);
  };

  // Helper function to construct proper image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';

    // For merchant brand logos, use the authenticated API endpoint
    if (imagePath.startsWith('uploads/merchant_brand_logos/')) {
      // Extract merchant_id and filename from the path
      // Path format: uploads/merchant_brand_logos/{merchant_id}/{filename}
      const parts = imagePath.split('/');
      if (parts.length >= 4) {
        const merchant_id = parts[2];
        const filename = parts.slice(3).join('/'); // Handle filenames with slashes
        return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/merchant_brand_logos/${merchant_id}/${filename}`;
      }
    }

    // For brand logos, use the authenticated API endpoint
    if (imagePath.startsWith('uploads/brand_logos/')) {
      const filename = imagePath.replace('uploads/brand_logos/', '');
      return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/brand_logos/${filename}`;
    }

    // For other uploads, use the authenticated API endpoint
    if (imagePath.startsWith('uploads/')) {
      return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/${imagePath.replace('uploads/', '')}`;
    }

    // Fallback: add the uploads prefix and use authenticated endpoint
    return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/${imagePath}`;
  };

  const [pollingCancelled, setPollingCancelled] = useState(false);
  const [pollingTimeout, setPollingTimeout] = useState(null);
  const [options, setOptions] = useState(null);
  const [originalOptions, setOriginalOptions] = useState(null); // Store original options for restoration
  const [uploadedFiles, setUploadedFiles] = useState([]); // Changed from uploadedFile to uploadedFiles array
  const [uploadPreviews, setUploadPreviews] = useState([]); // Changed from uploadPreview to uploadPreviews array
  const [allSelectedFiles, setAllSelectedFiles] = useState([]); // All files including invalid ones
  const [fileValidationStatus, setFileValidationStatus] = useState({}); // Track validation status for each file
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(false);
  const [isLoadingFormData, setIsLoadingFormData] = useState(false);
  const [isCaptionPopupOpen, setIsCaptionPopupOpen] = useState(false);
  const [captionsList, setCaptionsList] = useState([]);
  const [generationParams, setGenerationParams] = useState({
    category: '',
    model_type: '',
    output_format: '', // Will be set to image format ID after options load
    bust_color: '',
    ethnicity: '',
    clothing_type: '',
    bust_finish: '',
    gender_option_id: '', // Gender option (shown based on category_gender_mappings)
    theme: '', // Theme for product-only model types
    watermark: false, // Default to unchecked (no Glammify watermark)
    brand_watermark: false,
    add_caption: false,
    captions_data: [],
    caption_text_1: '',
    caption_text_2: '',
    media_quality_id: '', // Changed from media_quality string to media_quality_id
    custom_tags: [],
    selected_ai_model: '' // model_face_image_id (assigned model)
  });
  const [assignedAiModels, setAssignedAiModels] = useState([]);

  // Media qualities filtered by selected output format (IMAGE/GIF vs VIDEO).
  // If output format is not selected yet, default to IMAGE-mapped qualities.
  const filteredMediaQualities = useMemo(() => {
    if (!options?.media_qualities?.length) return [];
    const fmt = generationParams.output_format
      ? options.output_formats?.find(
        (of) => of.id === parseInt(generationParams.output_format, 10)
      )
      : null;
    const scope = generationParams.output_format
      ? mediaQualityAppliesScope(fmt?.name)
      : 'IMAGE';
    return options.media_qualities
      .filter((q) => q.status !== 0 && q.status !== '0')
      .filter((q) => (q.applies_to || 'IMAGE') === scope)
      .slice()
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [options?.media_qualities, options?.output_formats, generationParams.output_format]);

  // Keep media_quality_id valid when output format/options change (or when we use IMAGE fallback).
  useEffect(() => {
    if (!options?.media_qualities?.length) return;
    const list = filteredMediaQualities;
    if (!list.length) return;
    const valid = new Set(list.map((q) => q.id).filter(Boolean));
    const cur = generationParams.media_quality_id;
    if (valid.has(cur)) return;
    const first = list[0];
    const next = first.id;
    if (next) {
      setGenerationParams((prev) => ({ ...prev, media_quality_id: next }));
    }
  }, [
    filteredMediaQualities,
    generationParams.output_format,
    generationParams.media_quality_id,
    options?.media_qualities
  ]);
  const [tagInput, setTagInput] = useState('');
  const [customTagSuggestions, setCustomTagSuggestions] = useState([]);
  const [isTagSuggestionsLoading, setIsTagSuggestionsLoading] = useState(false);
  const [isTagInputFocused, setIsTagInputFocused] = useState(false);

  // Client-side NSFW (nsfwjs) removed — avoids heavy tfjs install/build on prod; use backend moderation if needed.
  const ensureImageSafety = async () => ({ ok: true });

  const minTagSuggestChars = 2;

  const [generationResult, setGenerationResult] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  // const [processingStatus, setProcessingStatus] = useState({ id: null, status: 'processing', message: '' });
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [completionGeneration, setCompletionGeneration] = useState(null);
  const [feedbackDialog, setFeedbackDialog] = useState(false);
  const [feedback, setFeedback] = useState({ satisfactory: null, comments: '' });
  const [successMessage, setSuccessMessage] = useState('');
  const [batchProgress, setBatchProgress] = useState({ completed: 0, total: 0, statuses: {}, batchId: null, failed: 0 });
  const [batchStatusDialog, setBatchStatusDialog] = useState(false);
  const [isBatchCompleted, setIsBatchCompleted] = useState(false);
  // Brand logo state
  const [brandLogos, setBrandLogos] = useState([]);
  const [selectedBrandLogoId, setSelectedBrandLogoId] = useState('');

  // Toggle tab state
  const [activePhotoTab, setActivePhotoTab] = useState('product');

  // Customer photo state
  const [customerFile, setCustomerFile] = useState(null);
  const [customerPreview, setCustomerPreview] = useState(null);
  const [customerUploadError, setCustomerUploadError] = useState('');
  const [customerSafetyBlocked, setCustomerSafetyBlocked] = useState(false);
  const customerFileInputRef = useRef(null);
  const latestTagSuggestionRequestRef = useRef(0);
  // Cache suggestions per normalized query to avoid repeated API calls for the same text.
  const customTagSuggestCacheRef = useRef(new Map());
  // Keep one in-flight request so we can cancel it when query changes quickly.
  const customTagSuggestAbortRef = useRef(null);

  // Validation dialog state
  const [validationDialog, setValidationDialog] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Guidelines popup state
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);

  // Field-specific validation errors
  const [fieldErrors, setFieldErrors] = useState({
    category: '',
    model_type: '',
    output_format: '',
    brand_watermark: '',
    ethnicity: '',
    clothing_type: '',
    bust_finish: '',
    bust_color: ''
  });

  // Watermark adjustment only — base generation costs come from v2 content matrix API
  const [creditCosts, setCreditCosts] = useState({
    watermark: 9,
    brand_logo: 0,
  });
  const [pricingError, setPricingError] = useState('');

  const [dynamicCredits, setDynamicCredits] = useState({
    base_cost: 0,
    total_cost: 0,
    file_count: 1,
    breakdown: {},
    output_format_name: ''
  });

  // Ignore stale / out-of-order responses from POST /calculate-credits (e.g. fast media quality changes)
  const creditsRequestSeqRef = useRef(0);

  const [creditBreakdown, setCreditBreakdown] = useState({
    base_cost: 0,
    watermark_cost: 0,
    brand_logo_cost: 0,
    model_type_multiplier: 1.0,

    category_multiplier: 1.0,
    material_multiplier: 1.0,
    quality_multiplier: 1.0,
    total_multiplier: 1.0
  });

  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const [bustColorOpen, setBustColorOpen] = useState(false);
  const [brandLogoOpen, setBrandLogoOpen] = useState(false);

  const handleInfoClick = (event) => {
    setInfoAnchorEl(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoAnchorEl(null);
  };

  const isInfoOpen = Boolean(infoAnchorEl);

  const { hasPermission, loading: permissionLoading } = useMerchantPermissions();

  const canViewGeneration = hasPermission('AI Generation', 'view');
  const canGenerateImage = hasPermission('AI Generation', 'generate_image');
  const canGenerateVideo = hasPermission('AI Generation', 'generate_video');
  const canAddBrand = hasPermission('AI Generation', 'add_brand');
  const canUseModelFace = hasPermission('AI Model Studio', 'generate');
  const canViewModelStudio = hasPermission('AI Model Studio', 'view');
  const canUseCustomerPhoto = hasPermission('Customer Photo', 'generate');
  // Batch generation (multiple file upload) requires the Bulk Upload module
  const canBatchGenerate = hasPermission('Bulk Upload', 'batch_generate');
  // Treat missing permissions as "not loaded yet" so the UI doesn't flicker hidden.
  const canUseModelFaceUi = permissionLoading ? true : canUseModelFace;

  const { merchant, refreshProfile } = useMerchantAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isCreatePage = location.pathname === '/generate';

  // Refresh profile on mount so stale localStorage balance doesn't trigger the
  // zero-credits banner before we have confirmed data from the server.
  useEffect(() => {
    refreshProfile().finally(() => setSubscriptionChecked(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Ensure we only apply prefill once
  const prefillAppliedRef = useRef(false);
  const fileInputRef = useRef(null);
  const loaderRef = useRef(null);
  const captionsContainerRef = useRef(null);
  const prevCaptionsLengthRef = useRef(captionsList.length);

  // Effect to scroll smoothly to the new caption section
  useEffect(() => {
    if (captionsList.length > prevCaptionsLengthRef.current && captionsContainerRef.current) {
      const container = captionsContainerRef.current;
      // Use a small timeout to ensure React has updated the DOM with the new entry
      setTimeout(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
    prevCaptionsLengthRef.current = captionsList.length;
  }, [captionsList.length]);

  // Load brand logos when component mounts
  useEffect(() => {
    if (merchant) {
      loadBrandLogos();
    }
  }, [merchant]);

  // Handle restoring form data returning from AI Model Studio.
  // We only restore when user came from the explicit "Go to AI Model Studio" button flow.
  useEffect(() => {
    const draftFromRouteState = location.state?.aiGenerationDraft || null;
    const draftFromWindow = getModelStudioDraft();
    const shouldRestoreFromModelStudio =
      Boolean(location.state?.fromModelStudio) ||
      Boolean(draftFromRouteState) ||
      Boolean(draftFromWindow) ||
      (() => {
        try {
          return localStorage.getItem(AI_MODEL_STUDIO_RETURN_KEY) === '1';
        } catch (_) {
          return false;
        }
      })();

    if (!shouldRestoreFromModelStudio) {
      // Ensure stale drafts never affect unrelated generate visits.
      clearModelStudioReturnDraft();
      return;
    }


    // Prefer route/window draft first so product/customer image selections remain intact.
    const richDraft = draftFromRouteState || draftFromWindow;
    if (richDraft) {
      const draft = richDraft;
      if (draft.generationParams) {
        setGenerationParams(prev => ({ ...prev, ...draft.generationParams }));
      }
      if (draft.activePhotoTab) {
        setActivePhotoTab(draft.activePhotoTab);
      }
      if (Array.isArray(draft.captionsList)) {
        setCaptionsList(draft.captionsList);
      }
      if (draft.selectedBrandLogoId) {
        setSelectedBrandLogoId(draft.selectedBrandLogoId);
      }
      if (Array.isArray(draft.uploadedFiles)) {
        setUploadedFiles(draft.uploadedFiles);
      }
      if (Array.isArray(draft.uploadPreviews)) {
        setUploadPreviews(draft.uploadPreviews);
      }
      if (Array.isArray(draft.allSelectedFiles)) {
        setAllSelectedFiles(draft.allSelectedFiles);
      }
      if (draft.fileValidationStatus && typeof draft.fileValidationStatus === 'object') {
        setFileValidationStatus(draft.fileValidationStatus);
      }
      if (draft.customerFile !== undefined) {
        setCustomerFile(draft.customerFile);
      }
      if (draft.customerPreview !== undefined) {
        setCustomerPreview(draft.customerPreview);
      }
      if (draft.customerUploadError !== undefined) {
        setCustomerUploadError(draft.customerUploadError);
      }
    }

    let savedStateStr = null;
    try {
      savedStateStr = sessionStorage.getItem('ai_generation_restore_state');
    } catch (e) {
      console.warn('sessionStorage read failed:', e);
    }
    if (savedStateStr) {
      try {
        const savedState = JSON.parse(savedStateStr);
        if (savedState.generationParams) {
          setGenerationParams(prev => ({ ...prev, ...savedState.generationParams }));
        }
        if (savedState.activePhotoTab) {
          setActivePhotoTab(savedState.activePhotoTab);
        }
        // captionsList is only saved in the full (non-fallback) snapshot
        if (Array.isArray(savedState.captionsList) && savedState.captionsList.length > 0) {
          setCaptionsList(savedState.captionsList);
        }
        if (savedState.selectedBrandLogoId) {
          setSelectedBrandLogoId(savedState.selectedBrandLogoId);
        }
      } catch (e) {
        console.error('Failed to restore AI generation state:', e);
      } finally {
        // Always clean up so stale data never persists
        try { sessionStorage.removeItem('ai_generation_restore_state'); } catch (_) { }
      }
    }
    clearModelStudioReturnDraft();

    // Re-fetch brand logos when returning from the Brands page so newly added logos appear immediately.
    if (location.state?.refreshBrandLogos) {
      loadBrandLogos();
    }

    // Make restore single-use even across browser history navigation.
    if (location.state?.fromModelStudio || location.state?.aiGenerationDraft) {
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  // Handle regenerate functionality - read URL parameters and pre-fill form
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isRegenerate = searchParams.get('regenerate') === 'true';

    // console.log('Prefill effect running:', { 
    //   isRegenerate, 
    //   prefillApplied: prefillAppliedRef.current, 
    //   optionsLoaded: !!options,
    //   hasCategories: !!options?.categories,
    //   hasMaterials: !!options?.materials
    // });

    // Only proceed if this is a regenerate flow and we haven't applied it yet
    if (!isRegenerate || prefillAppliedRef.current) return;

    // Show loading state and scroll to top for regenerate flow
    if (isRegenerate) {
      setIsLoadingFormData(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Wait until options are fully loaded with actual data
    if (!options || !options.categories || !options.model_types) {
      console.log('Waiting for options to load...', options);
      // Keep loading state active while waiting for options
      return;
    }

    const prefillData = {};
    for (const [key, value] of searchParams.entries()) {
      if (key !== 'regenerate' && key !== 'from_generation_id' && value !== '') {
        prefillData[key] = value;
      }
    }

    console.log('🔄 Starting prefill with:', {
      urlParams: Object.fromEntries(searchParams.entries()),
      prefillData,
      availableOptions: {
        categories: options.categories?.length || 0,
        materials: options.materials?.length || 0,
        modelTypes: options.model_types?.length || 0
      }
    });

    // Update generation parameters with pre-filled data (image excluded)
    const newParams = {
      category: prefillData.category_id || '',
      model_type: prefillData.model_type_id || '',
      material: prefillData.material_id || '',
      bust_color: prefillData.bust_color_id || '',
      ethnicity: (prefillData.model_face_image_id || prefillData.selected_ai_model) ? '' : (prefillData.ethnicity_id || ''),
      skin_tone: prefillData.skin_tone_id || '',
      clothing_type: prefillData.clothing_type_id || '',
      bust_finish: prefillData.bust_finish_id || '',
      output_format: prefillData.output_format_id || '',
      gender_option_id: prefillData.gender_option_id || '',
      selected_ai_model: prefillData.model_face_image_id || prefillData.selected_ai_model || '',
      media_quality_id: prefillData.media_quality_id || prefillData.output_quality_id || '',
      custom_tags: prefillData.custom_tags
        ? String(prefillData.custom_tags).split(',').map(tag => tag.trim()).filter(Boolean)
        : [],
      watermark: String(prefillData.has_watermark) === 'true',
      brand_watermark: String(prefillData.has_brand_logo) === 'true',
      add_caption: !!(prefillData.caption_text_1 || prefillData.caption_text_2),
      caption_text_1: prefillData.caption_text_1 || '',
      caption_text_2: prefillData.caption_text_2 || ''
    };

    console.log('📝 Setting generation params:', newParams);

    // Consolidate all state updates into a single call to avoid batching issues
    setGenerationParams(prevParams => {
      const updatedParams = {
        ...prevParams,
        ...newParams
      };
      console.log('🔄 Updated generation params:', updatedParams);
      return updatedParams;
    });

    // Regenerate flow sets caption_text_* on generationParams, but the Add Caption dialog and submit()
    // read per-image state from captionsList — seed one row so the popup and API payload stay in sync.
    const urlCaption1 = String(prefillData.caption_text_1 ?? '').trim();
    const urlCaption2 = String(prefillData.caption_text_2 ?? '').trim();
    if (urlCaption1 || urlCaption2) {
      setCaptionsList([{
        id: Date.now(),
        imageIndex: 0,
        text: urlCaption1,
        text_1: urlCaption1,
        text_2: urlCaption2,
        preview: null
      }]);
    }

    if (prefillData.brand_logo_id) {
      setSelectedBrandLogoId(prefillData.brand_logo_id);
      console.log('🏷️ Set brand logo ID:', prefillData.brand_logo_id);
    }

    // Handle bust finish pre-filling - ensure bust colors are loaded
    if (prefillData.bust_finish_id && options?.bust_finishes) {
      // Find the bust finish to get its name for logging
      const bustFinish = options.bust_finishes.find(bf => bf.id === parseInt(prefillData.bust_finish_id));
      console.log('🔍 Pre-filling bust finish:', {
        bustFinishId: prefillData.bust_finish_id,
        bustFinishIdType: typeof prefillData.bust_finish_id,
        bustFinishIdParsed: parseInt(prefillData.bust_finish_id),
        bustFinishFound: !!bustFinish,
        bustFinishName: bustFinish?.display_name || 'Unknown',
        bustFinishIdFromOptions: bustFinish?.id,
        hasBustColors: !!options?.bust_colors,
        bustColorsCount: options?.bust_colors?.length || 0,
        allBustFinishes: options.bust_finishes.map(bf => ({ id: bf.id, name: bf.display_name }))
      });

      // If bust colors are available, ensure they're properly set
      if (options?.bust_colors && options.bust_colors.length > 0) {
        console.log('✅ Bust Display selected, bust colors available for pre-filled bust finish');
      } else {
        console.log('⚠️ No bust colors available, will need to fetch them');
      }
    }

    // Don't show success message - just silently prefill the form
    prefillAppliedRef.current = true; // prevent re-application
    console.log('✅ Prefill completed');

    // Hide loading state after form is populated
    setIsLoadingFormData(false);

    // Debug: Check the current state after a short delay
    setTimeout(() => {
      console.log('🔍 Post-prefill state check:', {
        bustFinish: generationParams.bust_finish,
        bustColor: generationParams.bust_color,
        modelType: generationParams.model_type,
        category: generationParams.category,
        material: generationParams.material
      });
    }, 200);

    // Clear URL params after successful prefill
    setTimeout(() => {
      window.history.replaceState({}, document.title, location.pathname);
    }, 100);
  }, [location.search, options]);

  // Cleanup loading state when component unmounts or regenerate flow is cancelled
  useEffect(() => {
    return () => {
      setIsLoadingFormData(false);
    };
  }, []);

  // Handle model type changes and ensure proper field visibility
  useEffect(() => {
    if (!options || !generationParams.model_type) return;

    const modelTypeId = parseInt(generationParams.model_type);
    // console.log('🔍 Model Type Changed:', {
    //   modelType: generationParams.model_type,
    //   modelTypeId,
    //   modelTypeFields: options?.model_type_fields?.[modelTypeId],
    //   bustColorVisible: getFieldVisibility('bust_color'),
    //   bustColorsAvailable: options?.bust_colors?.length || 0
    // });

    // If Bust Display is selected and bust colors are available, ensure they're loaded
    if (modelTypeId === 1 && options?.bust_colors && options.bust_colors.length > 0) {
      console.log('✅ Bust Display selected, bust colors available:', options.bust_colors.length);
    }
  }, [generationParams.model_type, options]);

  // Handle bust finish changes and ensure bust colors are loaded
  useEffect(() => {
    if (!options || !generationParams.bust_finish) return;

    console.log('🔍 Bust Finish Changed:', {
      bustFinish: generationParams.bust_finish,
      bustFinishType: typeof generationParams.bust_finish,
      hasBustColors: !!options?.bust_colors,
      bustColorsCount: options?.bust_colors?.length || 0,
      originalBustColorsCount: originalOptions?.bust_colors?.length || 0
    });

    // If bust finish is selected but no bust colors are available, try to fetch them
    if (generationParams.bust_finish && (!options?.bust_colors || options.bust_colors.length === 0)) {
      console.log('⚠️ Bust finish selected but no bust colors available, attempting to fetch...');

      // Try to fetch bust colors for this finish
      const fetchBustColors = async () => {
        try {
          const response = await aiGenerationAPI.getBustColorsByFinish(generationParams.bust_finish);
          console.log('🔍 Bust colors fetch response:', response.data);

          if (response.data && response.data.bust_colors && response.data.bust_colors.length > 0) {
            const planScoped = filterBustColorsByPlanAllowlist(response.data.bust_colors);
            setOptions(prev => ({
              ...prev,
              bust_colors: planScoped
            }));
            console.log('✅ Successfully fetched bust colors:', planScoped.length);
          } else {
            // Fall back to plan-allowed bust colors if fetch returns empty
            console.log('⚠️ Fetch returned empty, falling back to original bust colors');
            if (originalOptions?.bust_colors) {
              setOptions(prev => ({
                ...prev,
                bust_colors: originalOptions.bust_colors
              }));
            }
          }
        } catch (error) {
          console.error('❌ Failed to fetch bust colors:', error);
          if (originalOptions?.bust_colors) {
            setOptions(prev => ({
              ...prev,
              bust_colors: originalOptions.bust_colors
            }));
          }
        }
      };

      fetchBustColors();
    }
  }, [generationParams.bust_finish, options, originalOptions]);

  // Debug: Monitor generationParams changes
  useEffect(() => {
    // console.log('🔍 GenerationParams changed:', {
    //   bustFinish: generationParams.bust_finish,
    //   bustColor: generationParams.bust_color,
    //   modelType: generationParams.model_type,
    //   category: generationParams.category,
    //   material: generationParams.material
    // });
  }, [generationParams]);

  // Load brand logos from API
  const loadBrandLogos = async () => {
    try {
      const response = await merchantAuthAPI.getBrandLogos();
      setBrandLogos(response.data.brand_logos || []);
    } catch (error) {
      console.error('Failed to load brand logos:', error);
    }
  };

  // Cleanup effect to clear polling timeout on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeout) {
        console.log('🧹 Cleaning up polling timeout on unmount');
        clearTimeout(pollingTimeout);
      }
    };
  }, [pollingTimeout]);

  // Scroll to top and loader only when processing first starts (not on every status update)
  useEffect(() => {
    if (processingStatus && !processingStatus.id) {
      // Only scroll when processingStatus is first set (id is null during initialization)
      // Add a small delay to ensure the DOM is fully updated
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);

      // Also scroll to loader element if available (for mobile centering)
      if (loaderRef.current) {
        // Check if it's a mobile device
        const isMobile = window.innerWidth < 768; // md breakpoint
        if (isMobile) {
          // Add a small delay to ensure the loader is rendered
          setTimeout(() => {
            loaderRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }, 150);
        }
      }
    }
  }, [processingStatus]);

  // Helper function to get icon for model type (dynamic from database)
  const getModelTypeIcon = (modelType) => {
    // If it's a model type object with preview_image_url, use it
    if (modelType && typeof modelType === 'object' && modelType.preview_image_url) {
      return (
        <img
          src={getImageUrl(modelType.preview_image_url)}
          alt={modelType.display_name || modelType.name}
          style={{ width: '20px', height: '20px', marginRight: '0px', objectFit: 'contain', borderRadius: '4px' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }
    // If it's just a name string (legacy), fall back to static icons
    if (typeof modelType === 'string') {
      switch (modelType) {
        case 'BUST':
          return <Accessibility sx={{ mr: 1, fontSize: 18 }} />;
        case 'HUMAN_MODEL':
          return <Person sx={{ mr: 1, fontSize: 18 }} />;
        case 'PLAIN_SURFACE':
          return <Crop sx={{ mr: 1, fontSize: 18 }} />;
        case 'BOX':
          return <Inventory2 sx={{ mr: 1, fontSize: 18 }} />;
        default:
          return null;
      }
    }
    // If it's a model type object with icon field, use dynamic icon
    if (modelType && modelType.icon) {
      try {
        const IconComponent = require('@mui/icons-material')[modelType.icon];
        return IconComponent ? <IconComponent sx={{ mr: 1, fontSize: 18 }} /> : null;
      } catch (e) {
        console.warn('Icon not found:', modelType.icon);
        // Fall back to static icon based on name
        return getModelTypeIcon(modelType.name);
      }
    }
    // Fall back to legacy behavior for model type name
    if (modelType?.name) {
      return getModelTypeIcon(modelType.name);
    }
    return null;
  };

  // Helper function to get icon for output format (dynamic from database)
  const getOutputFormatIcon = (formatObj) => {
    try {
      // If it's an output format object with preview_image_url, use it
      if (formatObj && typeof formatObj === 'object' && formatObj.preview_image_url) {
        return (
          <img
            src={getImageUrl(formatObj.preview_image_url)}
            alt={formatObj.display_name || formatObj.name}
            style={{ width: '20px', height: '20px', marginRight: '0px', objectFit: 'contain', borderRadius: '4px' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        );
      }

      // If it's an output format object with icon field, use dynamic icon
      if (formatObj && typeof formatObj === 'object' && formatObj.icon) {
        const IconComponent = require('@mui/icons-material')[formatObj.icon];
        if (IconComponent) {
          return <IconComponent sx={{ mr: 1, fontSize: 18 }} />;
        }
      }

      // Fall back to legacy behavior for output format name
      const formatName = typeof formatObj === 'string' ? formatObj : formatObj?.name;

      switch (formatName) {
        case 'IMAGE':
          return <ImageIcon sx={{ mr: 1, fontSize: 18 }} />;
        case 'VIDEO':
          return <Videocam sx={{ mr: 1, fontSize: 18 }} />;
        case 'GIF':
          return <Gif sx={{ mr: 1, fontSize: 18 }} />;
        default:
          return <InsertDriveFile sx={{ mr: 1, fontSize: 18 }} />; // Default file icon
      }
    } catch (error) {
      console.warn('Error loading output format icon:', error);
      return <InsertDriveFile sx={{ mr: 1, fontSize: 18 }} />; // Fallback icon
    }
  };

  // Helper function to validate file type and size
  const validateImageFile = (file, maxSizeMB = 12) => {
    // Check file size first
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `File size should be less than ${maxSizeMB}MB`;
    }

    // Check file extension (case-insensitive)
    const allowedExtensions = ['png', 'jpg', 'jpeg', 'webp', 'heic'];
    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      return 'Only PNG, JPG, JPEG, WebP, and HEIC files are allowed';
    }

    // Additional MIME type validation for extra security
    const allowedMimeTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp',
      'image/heic',
      'image/heif'
    ];

    // Only check MIME type if it's available and not empty
    if (file.type && file.type !== '' && !allowedMimeTypes.includes(file.type)) {
      return 'Only PNG, JPG, JPEG, WebP, and HEIC files are allowed';
    }

    return null; // No error
  };

  // Helper function to validate multiple file upload limits
  const validateMultipleFileUpload = (files) => {
    if (files.length > 1 && !canBatchGenerate) {
      return 'Batch generation requires a Lite plan or higher. Please upgrade your plan to upload multiple images at once.';
    }

    const maxFiles = 20;
    if (files.length > maxFiles) {
      return `Maximum ${maxFiles} images can be uploaded at once. Please select fewer files.`;
    }

    return null; // No error
  };

  // Show validation error dialog
  const showValidationError = (errorMessage) => {
    setValidationError(errorMessage);
    setValidationDialog(true);
  };

  const isChainMaleSelection = () => {
    const categoryId = generationParams.category ? parseInt(generationParams.category) : null;
    const selectedCategory = options?.categories?.find(c => c.id === categoryId);
    const categoryName = (selectedCategory?.name || selectedCategory?.display_name || '').toLowerCase();
    const genderId = generationParams.gender_option_id ? parseInt(generationParams.gender_option_id) : null;
    const selectedGender = options?.gender_options?.find(g => g.id === genderId);
    const genderName = (selectedGender?.name || selectedGender?.display_name || '').toLowerCase();
    return categoryName === 'chain' && genderName === 'male';
  };

  // Plan-scoped options: /options already excludes items outside the merchant plan;
  // further narrow by category + model_type mapping when present.
  const getFilteredOptions = (optionType) => {
    if (!options || !generationParams.model_type) return [];

    const modelTypeId = parseInt(generationParams.model_type);
    const categoryId = generationParams.category ? parseInt(generationParams.category) : null;

    const allOptionsMap = {
      ethnicities: options.ethnicities || [],
      clothing_types: options.clothing_types || [],
      bust_finishes: options.bust_finishes || [],
      bust_colors: options.bust_colors || [],
    };

    const allOptions = allOptionsMap[optionType] || [];

    const idFieldMap = {
      ethnicities: 'ethnicity_ids',
      clothing_types: 'clothing_type_ids',
      bust_finishes: 'bust_finish_ids',
      bust_colors: 'bust_color_ids',
    };

    const idField = idFieldMap[optionType];
    let scopedOptions = allOptions;

    if (categoryId && options.category_field_visibility) {
      const categoryKey = `${categoryId}_${modelTypeId}`;
      const categoryVisibility = options.category_field_visibility[categoryKey];

      if (categoryVisibility?.[idField]?.length > 0) {
        scopedOptions = allOptions.filter((opt) =>
          categoryVisibility[idField].includes(opt.id)
        );
      }
    }

    if (optionType === 'clothing_types' && isChainMaleSelection()) {
      const allowed = new Set(['casual', 'formal']);
      return scopedOptions.filter(opt => allowed.has((opt.display_name || opt.name || '').toLowerCase()));
    }

    return scopedOptions;
  };

  /** Intersect finish-specific bust colors with plan allowlist from /options. */
  const filterBustColorsByPlanAllowlist = (bustColors) => {
    const planAllowed = originalOptions?.bust_colors;
    if (!planAllowed?.length) return bustColors || [];
    const ids = new Set(planAllowed.map((c) => c.id));
    return (bustColors || []).filter((c) => ids.has(c.id));
  };

  // Dynamic field visibility based on backend category mappings
  // Only shows fields that are explicitly mapped for the selected category + model_type
  const getFieldVisibility = (fieldName) => {
    if (!options || !generationParams.model_type) return false;

    const modelTypeId = parseInt(generationParams.model_type);
    const categoryId = generationParams.category ? parseInt(generationParams.category) : null;

    // Handle gender - it's based on category mapping AND model type support
    if (fieldName === 'gender' || fieldName === 'gender_option_id') {
      const hasCategoryMapping = categoryId && options?.category_gender_visibility?.[String(categoryId)]?.length > 0;
      const modelSupportsGender = options?.model_type_fields?.[modelTypeId]?.gender;
      return !!(hasCategoryMapping && modelSupportsGender);
    }

    let isVisible = false;

    // If we have a category selected, use category-specific visibility
    if (categoryId && options.category_field_visibility) {
      const categoryKey = `${categoryId}_${modelTypeId}`;
      const categoryVisibility = options.category_field_visibility[categoryKey];

      if (categoryVisibility !== undefined) {
        isVisible = categoryVisibility[fieldName] || false;
      }
    } else {
      // Fallback to model_type_fields ONLY when no category is selected
      isVisible = options.model_type_fields?.[modelTypeId]?.[fieldName] || false;
    }

    // Hide plan-restricted fields when no allowed options remain for this category/model
    const planOptionTypeByField = {
      ethnicity: 'ethnicities',
      clothing_type: 'clothing_types',
      bust_finish: 'bust_finishes',
      bust_color: 'bust_colors',
    };
    const optionType = planOptionTypeByField[fieldName];
    if (optionType) {
      return isVisible && getFilteredOptions(optionType).length > 0;
    }

    return isVisible;
  };

  // Get Female gender option ID for defaulting
  const getFemaleOptionId = (opts = options) => {
    if (!opts?.gender_options) return '';
    const femaleOption = opts.gender_options.find(
      g => g.name?.toLowerCase() === 'female' || g.display_name?.toLowerCase() === 'female'
    );
    return femaleOption ? femaleOption.id.toString() : '';
  };

  // Check if the selected model type is "Product Only"
  const isProductOnlyModel = () => {
    const modelTypeId = parseInt(generationParams.model_type);
    if (!modelTypeId || !options?.model_types) return false;

    const selectedModelType = options?.model_types?.find(mt => mt.id === modelTypeId);
    if (!selectedModelType) return false;

    return selectedModelType.name.toLowerCase() === 'product_only' ||
           selectedModelType.display_name.toLowerCase() === 'product only';
  };

  // Validate required fields
  const validateRequiredFields = () => {
    const errors = {};

    if (!generationParams.category) {
      errors.category = 'Category is required';
    }

    if (!generationParams.model_type) {
      const humanId = activePhotoTab === 'customer' ? findHumanModelTypeId(options?.model_types) : null;
      if (!humanId) {
        errors.model_type =
          activePhotoTab === 'customer'
            ? 'Model options are still loading. Please wait a moment.'
            : 'Model type is required';
      }
    }

    if (!generationParams.output_format) {
      errors.output_format = 'Output format is required';
    }

    // Validate brand watermark requirements
    if (generationParams.brand_watermark && !selectedBrandLogoId) {
      errors.brand_watermark = 'Please select a brand logo for watermark';
    }

    // Validate dynamic fields based on visibility
    const dynamicFields = ['ethnicity', 'clothing_type', 'bust_finish', 'bust_color', 'gender_option_id'];
    for (const field of dynamicFields) {
      if (
        field === 'ethnicity' &&
        (generationParams.selected_ai_model ||
          (isHumanHandSelected && !allowEthnicityForFemaleRing) ||
          activePhotoTab === 'customer')
      ) {
        // Ethnicity is hidden for selected AI model flow, most Human Hand flows, and Customer Photo tab; do not require it.
        continue;
      }
      if (getFieldVisibility(field) && !generationParams[field]) {
        // Format field name for display (e.g. 'clothing_type' -> 'Clothing type')
        const displayName = field === 'gender_option_id' ? 'Gender' : field.split('_').map((word, index) =>
          index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
        ).join(' ');
        errors[field] = `${displayName} is required`;
      }
    }

    // Validate theme for Product Only models
    if (isProductOnlyModel() && options?.themes?.length > 0 && !generationParams.theme) {
      errors.theme = 'Theme is required for Product Only models';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    loadOptions();
    loadBrandLogos();
  }, []);

  const loadAssignedModels = useCallback(async () => {
    try {
      const resp = await merchantModelFaceAPI.listAssignedModels();
      const rows = resp?.data?.data || [];
      const arr = Array.isArray(rows) ? rows : [];
      // Server already omits expired rows; filter again so stale clients never show expired models.
      const nowMs = Date.now();
      const active = arr.filter((m) => {
        const exp = m?.access_expires_at;
        if (!exp) return true;
        const t = Date.parse(exp);
        return !Number.isNaN(t) && t > nowMs;
      });
      setAssignedAiModels(active);
      setGenerationParams((prev) => {
        const sel = prev.selected_ai_model;
        if (!sel) return prev;
        const still = active.some((x) => String(x.id) === String(sel));
        if (still) return prev;

        // If we are in regenerate flow and haven't applied prefill yet, don't clear it.
        const searchParams = new URLSearchParams(window.location.search);
        const isRegenerating = searchParams.get('regenerate') === 'true';
        if (isRegenerating && !prefillAppliedRef.current) return prev;

        return { ...prev, selected_ai_model: '' };
      });
    } catch (e) {
      console.error('❌ Failed to load assigned AI models:', e);
      setAssignedAiModels([]);
    }
  }, []);

  useEffect(() => {
    loadAssignedModels();
  }, [loadAssignedModels]);

  useEffect(() => {
    if (location.state?.fromModelStudio) {
      // Refresh "Select Your AI Model" data after returning from studio purchase flow.
      loadAssignedModels();
    }
  }, [location.state, loadAssignedModels]);

  // Scroll to top whenever the page is (re-)visited.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Prevent focus-based scroll jumps (e.g. MUI inputs grabbing focus and shifting the page).
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    const preventFocusScroll = () => {
      const scrollY = window.scrollY;
      setTimeout(() => {
        if (window.scrollY !== scrollY) {
          window.scrollTo(0, scrollY);
        }
      }, 0);
    };
    document.addEventListener('focusin', preventFocusScroll);
    window.addEventListener('resize', preventFocusScroll);
    return () => {
      document.removeEventListener('focusin', preventFocusScroll);
      window.removeEventListener('resize', preventFocusScroll);
    };
  }, []);

  const loadOptions = async () => {
    try {
      // Get merchant's industry type from profile
      const industryTypeId = merchant?.industry_type_id || null;

      // Get all options without category filtering
      const response = await aiGenerationAPI.getOptions(industryTypeId);
      console.log('🔍 Options API Full Response:', response);
      console.log('📋 Loaded options data:', response.data.options);
      setOptions(response.data.options);
      setOriginalOptions(response.data.options); // Store original options for restoration

      const femaleId = getFemaleOptionId(response.data.options);
      if (femaleId) {
        setGenerationParams(prev => ({
          ...prev,
          gender_option_id: prev.gender_option_id || femaleId
        }));
      }


      console.log('📋 FULL OPTIONS LOADED:', response.data.options);
      console.log('📋 GENDER OPTIONS:', response.data.options?.gender_options);
      console.log('📋 GENDER VISIBILITY:', response.data.options?.category_gender_visibility);

      // Debug: Log the options structure to understand field visibility
      // console.log('🔍 Options loaded:', {
      //   categories: response.data.options?.categories?.length || 0,
      //   materials: response.data.options?.materials?.length || 0,
      //   modelTypes: response.data.options?.model_types?.length || 0,
      //   bustColors: response.data.options?.bust_colors?.length || 0,
      //   modelTypeFields: response.data.options?.model_type_fields,
      //   fullOptions: response.data.options
      // });

      // No default output format selection - user must choose
      // Removed automatic default selection of image format

      // Fetch credit settings
      try {
        const creditSettingsResponse = await aiGenerationAPI.getCreditSettings();
        if (creditSettingsResponse.data?.credit_settings) {
          const settings = creditSettingsResponse.data.credit_settings;
          setCreditCosts({
            watermark: settings.watermark_cost ?? 9,
            brand_logo: settings.brand_logo_cost ?? 0,
          });
        }
      } catch (creditError) {
        console.error('Failed to load credit settings:', creditError);
        // Continue with default values
      }
    } catch (error) {
      console.error('Failed to load options:', error);
      setError('Failed to load generation options');
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      // Clear any existing upload error when user selects files
      if (uploadError) {
        setUploadError('');
      }
      // Check Flutter permissions on mobile before processing files
      if (FlutterServicesAPI.isFlutterApp()) {
        try {
          console.log('📱 Checking file permissions on mobile...');
          const permissionResult = await FlutterServicesAPI.checkFilePermissions();
          console.log('🔐 Permission result:', permissionResult);
          if (!permissionResult.can_upload) {
            console.log('❌ File upload blocked - no permissions');
            // setUploadError('File upload permission denied. Please check your device permissions.');
            return;
          }
          console.log('✅ File upload permission granted');
        } catch (error) {
          console.error('❌ Error checking file permissions:', error);
          // setUploadError('Failed to check file permissions. Please try again.');
          return;
        }
      }
      await processFiles(files);
    }
  };

  const processFiles = async (files) => {
    // First validate multiple file upload limits
    const multipleFileError = validateMultipleFileUpload(files);
    if (multipleFileError) {
      setUploadError(multipleFileError);
      return;
    }

    const validFiles = [];
    const errors = [];
    const invalidFiles = [];
    const newValidationStatus = { ...fileValidationStatus };
    const allFiles = [...allSelectedFiles];

    // Process each file individually
    for (const file of files) {
      console.log(`Processing file: ${file.name}, type: ${file.type}, size: ${file.size}`);

      // 1. Basic validation (type/size)
      const validationError = validateImageFile(file, 12);

      if (validationError) {
        console.log(`File ${file.name} is invalid: ${validationError}`);
        errors.push(`File ${file.name}: ${validationError}`);
        invalidFiles.push(file);
        newValidationStatus[file.name] = { valid: false, error: validationError };
        allFiles.push(file);
        continue;
      }

      // 2. Safety check (NSFW) — extreme Porn/Hentai only (see evaluateNsfwSafety)
      const safetyResult = await ensureImageSafety(file);
      if (!safetyResult.ok) {
        invalidFiles.push(file);
        newValidationStatus[file.name] = { valid: false, error: null, blockType: 'safety' };
        allFiles.push(file);
        continue;
      }

      // File is valid - add to valid files and create preview
      console.log(`File ${file.name} is valid and safe, creating preview`);
      validFiles.push(file);
      newValidationStatus[file.name] = { valid: true, error: null };
      allFiles.push(file);
    }

    console.log(`Validation results: ${validFiles.length} valid, ${invalidFiles.length} invalid`);

    // Update state first
    setAllSelectedFiles(allFiles);
    setFileValidationStatus(newValidationStatus);

    // Object URLs render immediately — avoids a flash of "Invalid Image" while FileReader loads.
    const newPreviews = [...uploadPreviews];
    while (newPreviews.length < allFiles.length) {
      newPreviews.push(null);
    }
    validFiles.forEach((file) => {
      const fileIndex = allFiles.findIndex((f) => f.name === file.name);
      if (fileIndex !== -1) {
        newPreviews[fileIndex] = URL.createObjectURL(file);
      }
    });
    setUploadPreviews(newPreviews);

    // Show non-NSFW errors for invalid files (NSFW errors are shown on the image preview itself)
    const nonNsfwErrors = errors.filter(err => !err.includes('NSFW'));
    if (nonNsfwErrors.length > 0) {
      setUploadError(nonNsfwErrors.join('\n'));
    } else {
      setUploadError('');
    }

    // Add valid files to the upload
    if (validFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...validFiles]);
      setError('');
    }

    // When every file failed, errors are shown on each preview — no summary banner.
    if (validFiles.length === 0 && invalidFiles.length > 0) {
      setUploadError('');
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
    // Clear any existing upload error when user drags files
    if (uploadError) {
      setUploadError('');
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Check Flutter permissions on mobile before processing files
      if (FlutterServicesAPI.isFlutterApp()) {
        try {
          console.log('📱 Checking file permissions on mobile (drag & drop)...');
          const permissionResult = await FlutterServicesAPI.checkFilePermissions();
          console.log('🔐 Permission result:', permissionResult);
          if (!permissionResult.can_upload) {
            console.log('❌ File upload blocked - no permissions');
            // setUploadError('File upload permission denied. Please check your device permissions.');
            return;
          }
          console.log('✅ File upload permission granted');
        } catch (error) {
          console.error('❌ Error checking file permissions:', error);
          // setUploadError('Failed to check file permissions. Please try again.');
          return;
        }
      }
      await processFiles(files);
    }
  };

  /**
   * Handle upload button click - check permissions before opening file dialog
   */
  const handleUploadButtonClick = async (event) => {
    // Prevent event bubbling to avoid multiple calls
    if (event) {
      event.stopPropagation();
    }
    // Check if already processing to prevent multiple calls
    if (isCheckingPermissions) {
      console.log('⏳ Permission check already in progress, skipping...');
      return;
    }
    // Check Flutter permissions on mobile before opening file dialog
    if (FlutterServicesAPI.isFlutterApp()) {
      try {
        setIsCheckingPermissions(true);
        console.log('📱 Checking file permissions on mobile (upload button)...');
        const permissionResult = await FlutterServicesAPI.checkFilePermissions();
        console.log('🔐 Permission result:', permissionResult);
        if (!permissionResult.can_upload) {
          console.log('❌ File upload blocked - no permissions');
          // setUploadError('File upload permission denied. Please check your device permissions.');
          return;
        }
        console.log('✅ File upload permission granted, opening file dialog');
      } catch (error) {
        console.error('❌ Error checking file permissions:', error);
        // setUploadError('Failed to check file permissions. Please try again.');
        return;
      } finally {
        setIsCheckingPermissions(false);
      }
    }
    // Open file dialog if permissions are granted or not in Flutter app
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCustomerFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setCustomerUploadError('');
      setCustomerSafetyBlocked(false);
      // Check Flutter permissions on mobile
      if (FlutterServicesAPI.isFlutterApp()) {
        try {
          const permissionResult = await FlutterServicesAPI.checkFilePermissions();
          if (!permissionResult.can_upload) {
            // setCustomerUploadError('File upload permission denied. Please check your device permissions.');
            return;
          }
        } catch (error) {
          // setCustomerUploadError('Failed to check file permissions. Please try again.');
          return;
        }
      }

      const validationError = validateImageFile(file, 12);
      if (validationError) {
        setCustomerUploadError(validationError);
        setCustomerSafetyBlocked(false);
        return;
      }

      // Customer photos: no override — any flagged score is a hard block
      const safetyResult = await ensureImageSafety(file);
      if (!safetyResult.ok) {
        setCustomerUploadError('');
        setCustomerSafetyBlocked(true);
        setCustomerFile(file);
        setCustomerPreview((prev) => {
          if (typeof prev === 'string' && prev.startsWith('blob:')) {
            URL.revokeObjectURL(prev);
          }
          return null;
        });
        return;
      }
      setCustomerSafetyBlocked(false);

      const reader = new FileReader();
      reader.onload = (e) => {
        setCustomerPreview(e.target.result);
        setCustomerFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCustomerFile = () => {
    setCustomerPreview((prev) => {
      if (typeof prev === 'string' && prev.startsWith('blob:')) {
        URL.revokeObjectURL(prev);
      }
      return null;
    });
    setCustomerFile(null);
    setCustomerUploadError('');
    setCustomerSafetyBlocked(false);
    if (customerFileInputRef.current) {
      customerFileInputRef.current.value = '';
    }
  };

  const handleCustomerUploadButtonClick = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (customerFileInputRef.current) {
      customerFileInputRef.current.click();
    }
  };

  const removeImage = (index) => {
    const fileToRemove = allSelectedFiles[index];
    if (!fileToRemove) return;
    console.log(`Removing file: ${fileToRemove.name}`);

    // Remove from all states
    setAllSelectedFiles(prev => prev.filter((_, i) => i !== index));

    // Remove from uploaded files if it was valid
    if (fileValidationStatus[fileToRemove.name]?.valid) {
      setUploadedFiles(prev => prev.filter(file => file !== fileToRemove));
    }

    // Remove from previews - IMPORTANT: Remove by index, not by filtering
    setUploadPreviews(prev => {
      const previewUrl = prev[index];
      if (typeof previewUrl === 'string' && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
      const newPreviews = [...prev];
      newPreviews.splice(index, 1);
      return newPreviews;
    });

    // Remove from validation status
    setFileValidationStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[fileToRemove.name];
      return newStatus;
    });
    setUploadError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const changeImage = async (index) => {
    // Check Flutter permissions on mobile before opening file dialog
    if (FlutterServicesAPI.isFlutterApp()) {
      try {
        console.log('📱 Checking file permissions on mobile (change image)...');
        const permissionResult = await FlutterServicesAPI.checkFilePermissions();
        console.log('🔐 Permission result:', permissionResult);
        if (!permissionResult.can_upload) {
          console.log('❌ File upload blocked - no permissions');
          // setUploadError('File upload permission denied. Please check your device permissions.');
          return;
        }
        console.log('✅ File upload permission granted, opening file dialog');
      } catch (error) {
        console.error('❌ Error checking file permissions:', error);
        // setUploadError('Failed to check file permissions. Please try again.');
        return;
      }
    }
    // Create a temporary file input for changing a specific image
    const tempFileInput = document.createElement('input');
    tempFileInput.type = 'file';
    tempFileInput.accept = '.png,.jpg,.jpeg,.webp,.heic';
    tempFileInput.multiple = false; // Only allow one file for replacement

    tempFileInput.onchange = async (event) => {
      const file = event.target.files[0];
      if (file) {
        console.log(`Changing image at index ${index} to: ${file.name}`);

        // 1. Basic Validation
        const validationError = validateImageFile(file, 12);
        const oldFile = allSelectedFiles[index];

        // 2. Safety Check (NSFW)
        let safetyBlockType = null;
        if (!validationError) {
          const safetyResult = await ensureImageSafety(file);
          if (!safetyResult.ok) {
            safetyBlockType = safetyResult.blockType;
          }
        }

        const finalError = validationError || safetyBlockType;

        if (finalError) {
          // Handle invalid/unsafe file
          console.log(`New file ${file.name} is invalid/unsafe: ${finalError}`);
          setFileValidationStatus((prev) => {
            const next = { ...prev };
            if (oldFile?.name) {
              delete next[oldFile.name];
            }
            next[file.name] = {
              valid: false,
              error: validationError || null,
              ...(safetyBlockType ? { blockType: safetyBlockType } : {}),
            };
            return next;
          });
          setAllSelectedFiles((prev) => {
            const newFiles = [...prev];
            newFiles[index] = file;
            return newFiles;
          });
          // Remove from valid files if it was previously valid
          if (oldFile && uploadedFiles.includes(oldFile)) {
            setUploadedFiles(prev => prev.filter(f => f !== oldFile));
          }
          // Remove preview if it exists
          setUploadPreviews(prev => {
            const newPreviews = [...prev];
            if (newPreviews[index]) {
              newPreviews[index] = null;
            }
            return newPreviews;
          });
          if (validationError) {
            setUploadError(`File ${file.name}: ${validationError}`);
          }
          setError('');
        } else {
          console.log(`New file ${file.name} is valid and safe, updating preview`);
          setAllSelectedFiles((prev) => {
            const newFiles = [...prev];
            newFiles[index] = file;
            return newFiles;
          });
          setFileValidationStatus((prev) => {
            const next = { ...prev };
            if (oldFile?.name) {
              delete next[oldFile.name];
            }
            next[file.name] = { valid: true, error: null };
            return next;
          });
          setUploadedFiles((prev) => {
            const newFiles = [...prev];
            const oldFileIndex = newFiles.findIndex((f) => f === oldFile);
            if (oldFileIndex !== -1) {
              newFiles[oldFileIndex] = file;
            } else {
              newFiles.push(file);
            }
            return newFiles;
          });
          setUploadPreviews((prev) => {
            const newPreviews = [...prev];
            const previousUrl = newPreviews[index];
            if (typeof previousUrl === 'string' && previousUrl.startsWith('blob:')) {
              URL.revokeObjectURL(previousUrl);
            }
            newPreviews[index] = URL.createObjectURL(file);
            return newPreviews;
          });
          setUploadError('');
          setError('');
        }
      }
    };
    tempFileInput.click();
  };

  const removeAllImages = () => {
    setUploadedFiles([]);
    setUploadPreviews([]);
    setAllSelectedFiles([]);
    setFileValidationStatus({});
    setUploadError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleMediaQualityChange = (qualityId) => {
    setGenerationParams(prev => ({ ...prev, media_quality_id: qualityId }));
  };

  /** Commit draft text in the custom-tags field as chip(s); supports comma-separated values and skips duplicates. */
  const commitTagInputValue = useCallback((rawValue) => {
    const value = String(rawValue ?? '').trim();
    if (!value) return;
    const newTags = value.split(',').map((tag) => tag.trim()).filter(Boolean);
    setGenerationParams((prev) => {
      const unique = newTags.filter((tag) => !prev.custom_tags.includes(tag));
      if (unique.length === 0) return prev;
      return { ...prev, custom_tags: [...prev.custom_tags, ...unique] };
    });
    setTagInput('');
  }, []);

  const handleTagInputChange = (e) => {
    const val = e.target.value;
    // Comma or space acts as delimiter immediately for mobile support where keydown is unreliable
    if (val.endsWith(',') || val.endsWith(' ')) {
      const tag = val.slice(0, -1).trim();
      if (tag) {
        commitTagInputValue(tag);
      } else {
        setTagInput('');
      }
    } else {
      setTagInput(val);
    }
  };

  const handleTagSuggestionSelect = (suggestedTag) => {
    commitTagInputValue(suggestedTag);
    setCustomTagSuggestions([]);
  };

  const handleEditTag = (tagToEdit) => {
    // Remove from list and put back in input for editing
    setGenerationParams((prev) => ({
      ...prev,
      custom_tags: prev.custom_tags.filter((t) => t !== tagToEdit)
    }));
    setTagInput(tagToEdit);
  };

  const handleTagInputKeyDown = (e) => {
    // Support Enter, Comma, Tab, and Space as delimiters
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab' || e.key === ' ') {
      if (tagInput.trim()) {
        e.preventDefault();
        commitTagInputValue(tagInput);
        setCustomTagSuggestions([]);
      } else if (e.key === ',') {
        // Prevent entering just a comma if input is empty
        e.preventDefault();
      }
    } else if (e.key === 'Backspace' && !tagInput && generationParams.custom_tags.length > 0) {
      // Remove last tag if backspace is pressed on empty input
      const newTags = [...generationParams.custom_tags];
      newTags.pop();
      setGenerationParams(prev => ({ ...prev, custom_tags: newTags }));
    } else if (e.key === 'Escape') {
      setCustomTagSuggestions([]);
    }
  };

  // Fetch custom-tag suggestions only while user is actively typing in the input.
  useEffect(() => {
    const query = tagInput.trim();
    if (!isTagInputFocused || query.length < minTagSuggestChars) {
      setCustomTagSuggestions([]);
      setIsTagSuggestionsLoading(false);
      if (customTagSuggestAbortRef.current) {
        customTagSuggestAbortRef.current.abort();
        customTagSuggestAbortRef.current = null;
      }
      return undefined;
    }

    const normalizedQuery = query.toLowerCase();
    const cachedSuggestions = customTagSuggestCacheRef.current.get(normalizedQuery);
    if (cachedSuggestions) {
      const existingTagSet = new Set(
        generationParams.custom_tags.map((tag) => String(tag).trim().toLowerCase())
      );
      setCustomTagSuggestions(
        cachedSuggestions.filter((tag) => !existingTagSet.has(String(tag).trim().toLowerCase()))
      );
      setIsTagSuggestionsLoading(false);
      return undefined;
    }

    const requestId = latestTagSuggestionRequestRef.current + 1;
    latestTagSuggestionRequestRef.current = requestId;

    const debounceId = setTimeout(async () => {
      try {
        setIsTagSuggestionsLoading(true);
        if (customTagSuggestAbortRef.current) {
          customTagSuggestAbortRef.current.abort();
        }
        const abortController = new AbortController();
        customTagSuggestAbortRef.current = abortController;
        const { data } = await aiGenerationAPI.getCustomTagSuggestions(
          { q: query, limit: 8 },
          { signal: abortController.signal }
        );
        if (requestId !== latestTagSuggestionRequestRef.current) return;
        const existingTagSet = new Set(
          generationParams.custom_tags.map((tag) => String(tag).trim().toLowerCase())
        );
        const fetchedSuggestions = Array.isArray(data?.suggestions) ? data.suggestions : [];
        customTagSuggestCacheRef.current.set(normalizedQuery, fetchedSuggestions);
        const filteredSuggestions = fetchedSuggestions.filter((tag) => {
          const normalizedTag = String(tag).trim().toLowerCase();
          return normalizedTag && !existingTagSet.has(normalizedTag);
        });
        setCustomTagSuggestions(filteredSuggestions);
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.name === 'AbortError') return;
        if (requestId !== latestTagSuggestionRequestRef.current) return;
        setCustomTagSuggestions([]);
      } finally {
        if (requestId === latestTagSuggestionRequestRef.current) {
          setIsTagSuggestionsLoading(false);
        }
      }
    }, 250);

    return () => {
      clearTimeout(debounceId);
      if (customTagSuggestAbortRef.current) {
        customTagSuggestAbortRef.current.abort();
        customTagSuggestAbortRef.current = null;
      }
    };
  }, [tagInput, isTagInputFocused, generationParams.custom_tags, minTagSuggestChars]);

  const handleRemoveTag = (tagToRemove) => {
    setGenerationParams(prev => ({
      ...prev,
      custom_tags: prev.custom_tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleModelTypeChange = (event) => {

    const value = event.target.value;

    const selectedMT = options?.model_types?.find(mt => mt.id === parseInt(value, 10));
    const isNewModelProductOnly = selectedMT?.name.toLowerCase() === 'product_only' || 
                                 selectedMT?.display_name.toLowerCase() === 'product only';

    setGenerationParams(prev => {
      const nextParams = {
        ...prev,
        model_type: value,
        // Reset dependent fields
        bust_color: '',
        ethnicity: '',
        clothing_type: '',
        bust_finish: '',
        gender_option_id: getFemaleOptionId(),
        theme: '' // Reset theme when model type changes
      };

      // If user selected Product Only, we don't allow VIDEO output.
      // Force it back to IMAGE if it was Video.
      if (isNewModelProductOnly) {
        const currentFormat = options?.output_formats?.find(of => of.id === parseInt(prev.output_format, 10));
        if (currentFormat?.name === 'VIDEO') {
          const imageFormat = options?.output_formats?.find(of => of.name === 'IMAGE');
          if (imageFormat) {
            nextParams.output_format = imageFormat.id.toString();
          }
        }
      }
      return nextParams;
    });

    // Clear model type error when selected
    setFieldErrors(prev => ({ ...prev, model_type: '' }));
  };


  const handleOutputFormatChange = (event) => {
    const value = event.target.value;

    const selectedFormat = options?.output_formats?.find(of => of.id === parseInt(value, 10));
    const scope = mediaQualityAppliesScope(selectedFormat?.name);
    const list = (options?.media_qualities || []).filter(
      (q) => q.status !== 0 && q.status !== '0' && (q.applies_to || 'IMAGE') === scope
    );
    const firstMq = list.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0];
    const firstName = firstMq?.name || firstMq?.display_name;

    setGenerationParams(prev => {
      const stillValid = list.some(
        (q) => q.id === prev.media_quality_id
      );
      return {
        ...prev,
        output_format: value,
        media_quality_id: stillValid ? prev.media_quality_id : (firstName || prev.media_quality_id),
      };
    });

    // Clear the output format error when a selection is made
    if (fieldErrors.output_format) {
      setFieldErrors(prev => ({
        ...prev,
        output_format: null
      }));
    }
  };

  const calculateCredits = () => dynamicCredits?.total_cost || 0;

  const calculateDynamicCredits = useCallback(async () => {
    // Changing the category resets model_type (see the category tile onClick), so
    // without this guard we'd ask for a price with model_type_id='' and the backend
    // would answer "No v2 content type mapping for model=None" — a red error the
    // user sees just for switching category, before picking a model again.
    // Addon flows price via addon_type instead, so they must not be blocked here.
    const hasAddonContext = activePhotoTab === 'customer' || !!generationParams.selected_ai_model;
    const canPrice = !!generationParams.model_type || hasAddonContext;

    if (!options || uploadedFiles.length === 0 || !generationParams.output_format || !canPrice) {
      setPricingError('');
      setDynamicCredits({ base_cost: 0, total_cost: 0, file_count: 1, breakdown: {}, output_format_name: '' });
      return;
    }

    const seq = ++creditsRequestSeqRef.current;

    try {
      const requestData = {
        output_format_id: generationParams.output_format,
        media_quality_id: generationParams.media_quality_id,
        has_watermark: generationParams.watermark,
        has_brand_logo: generationParams.brand_watermark || false,
        model_type_id: generationParams.model_type,
        category_id: generationParams.category,
        file_count: uploadedFiles.length,
        // Addon context for quality matrix pricing
        model_face_image_id: activePhotoTab === 'customer' ? null : (generationParams.selected_ai_model || null),
        customer_face_image_path: activePhotoTab === 'customer' ? 'customer_photo_placeholder' : null,
      };

      const response = await aiGenerationAPI.calculateCredits(requestData);

      if (seq !== creditsRequestSeqRef.current) {
        return;
      }

      if (response.data) {
        setDynamicCredits(response.data);
        setCreditBreakdown(response.data.breakdown || {});
        setPricingError('');
      }
    } catch (error) {
      if (seq === creditsRequestSeqRef.current) {
        const msg = error.response?.data?.error
          || 'Unable to calculate token cost for this selection. Please contact support.';
        setPricingError(msg);
        setDynamicCredits({ base_cost: 0, total_cost: 0, file_count: uploadedFiles.length, breakdown: {}, output_format_name: '' });
        console.error('❌ Error calculating dynamic credits:', error);
      }
    }
  }, [
    options,
    uploadedFiles.length,
    generationParams.output_format,
    generationParams.media_quality_id,
    generationParams.watermark,
    generationParams.brand_watermark,
    generationParams.model_type,
    generationParams.category,
    generationParams.selected_ai_model,
    activePhotoTab
  ]);

  // Recalculate credits when pricing inputs change (debounced; media_quality included via useCallback deps)
  useEffect(() => {
    if (!options || uploadedFiles.length === 0 || !generationParams.output_format) {
      return;
    }
    const timeoutId = setTimeout(() => {
      calculateDynamicCredits();
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [options, uploadedFiles.length, generationParams.output_format, calculateDynamicCredits]);

  const calculateMaxImagesPossible = () => {
    if (!options || uploadedFiles.length === 0) return 0;

    const balance = merchant?.credits_balance || 0;
    const totalCost = dynamicCredits?.total_cost > 0 ? dynamicCredits.total_cost : calculateCredits();

    if (totalCost === 0) return uploadedFiles.length; // If no cost, can process all images

    const creditCostPerImage = totalCost / uploadedFiles.length;

    return Math.floor(balance / creditCostPerImage);
  };

  const hasInsufficientCredits = () => {
    const balance = merchant?.credits_balance || 0;
    const cost = dynamicCredits?.total_cost > 0 ? dynamicCredits.total_cost : calculateCredits();
    return uploadedFiles.length > 0 && cost > 0 && balance < cost;
  };

  // Check if user's subscription is suspended or inactive
  const isSubscriptionSuspended = () => {
    const status = merchant?.subscription_info?.status;
    const isActive = merchant?.subscription_info?.is_active;

    // Check for suspended, cancelled, expired, grace, or inactive subscriptions
    return status === 'SUSPENDED' || status === 'CANCELLED' || status === 'EXPIRED' || status === 'GRACE' || !isActive;
  };

  // Check if user has zero credits balance
  const hasZeroCredits = () => {
    const balance = merchant?.credits_balance || 0;
    return balance <= 0;
  };

  // Free tokens are granted only after an admin approves the account
  const isAwaitingFreeTokens = () =>
    ['pending', 'rejected'].includes(merchant?.verification_status) && !merchant?.free_tokens_granted;

  const getVerificationStatusMessage = () => {
    const isFlutter = FlutterServicesAPI.isFlutterApp();
    const label = FlutterServicesAPI.getCreditLabel().toLowerCase();
    if (merchant?.verification_status === 'pending') {
      return {
        title: 'Pending Verification',
        message: `Your account is under review. Your free ${label} will be added once it's approved.` +
          (isFlutter ? '' : ' You can buy a plan or top up to start right away.')
      };
    }
    return {
      title: 'Verification not approved',
      message: `Free ${label} aren't available for this account.` +
        (isFlutter ? '' : ' Purchase a plan or top up to continue.')
    };
  };

  // Get subscription status message
  const getSubscriptionStatusMessage = () => {
    const isFlutter = FlutterServicesAPI.isFlutterApp();

    // During app review in Flutter, hide mention of subscription/credits
    if (!showWhatsAppSmsAuth && isFlutter) {
      return {
        title: 'Generate',
        message: 'Generation features are currently unavailable. Please contact support for more information.'
      };
    }

    // New merchants have no tokens until their account is verified
    if (hasZeroCredits() && isAwaitingFreeTokens()) {
      return getVerificationStatusMessage();
    }

    // Show different message for zero credits vs subscription suspended
    if (hasZeroCredits() && !isSubscriptionSuspended()) {
      return {
        title: isFlutter ? 'Access Restricted' : `${FlutterServicesAPI.getCreditLabel()} Exhausted`,
        message: `Your ${FlutterServicesAPI.getCreditLabel().toLowerCase()} balance is 0. Please purchase a package to continue generating content.`
      };
    }
    return {
      title: isFlutter ? 'Generate' : 'Subscription Inactive',
      message: 'Your subscription is currently inactive. You cannot generate new content until your subscription is active.'
    };
  };

  // Build all validation blockers in one place so button state + debug logs stay consistent.
  const getGenerateBlockingReasons = () => {
    const reasons = [];
    if (isSubscriptionSuspended()) reasons.push('subscription_suspended');
    if (hasZeroCredits()) reasons.push('zero_credits');

    // Basic required fields; on customer tab model type is forced to Human Model (hidden UI).
    const resolvedModelType =
      generationParams.model_type ||
      (activePhotoTab === 'customer' ? findHumanModelTypeId(options?.model_types) : '');
    if (!generationParams.category) reasons.push('missing_category');
    if (!resolvedModelType) reasons.push('missing_model_type');
    if (!generationParams.output_format) reasons.push('missing_output_format');

    if (generationParams.brand_watermark && !selectedBrandLogoId) {
      reasons.push('missing_brand_logo');
    }

    // Check dynamic fields based on visibility.
    const dynamicFields = ['ethnicity', 'clothing_type', 'bust_finish', 'bust_color', 'gender_option_id'];
    for (const field of dynamicFields) {
      if (
        field === 'ethnicity' &&
        (generationParams.selected_ai_model ||
          (isHumanHandSelected && !allowEthnicityForFemaleRing) ||
          activePhotoTab === 'customer')
      ) {
        // Ethnicity is hidden for selected AI model flow, most Human Hand flows, and Customer Photo tab; do not require it.
        continue;
      }
      if (getFieldVisibility(field) && !generationParams[field]) {
        reasons.push(`missing_${field}`);
      }
    }

    if (filteredMediaQualities.length > 0 && !generationParams.media_quality_id) {
      reasons.push('missing_media_quality');
    }

    if (hasInsufficientCredits()) reasons.push('insufficient_credits');
    if (pricingError) reasons.push('pricing_error');
    if (!pricingError && uploadedFiles.length > 0 && generationParams.output_format && dynamicCredits.total_cost <= 0) {
      reasons.push('pricing_pending');
    }

    // Theme is mandatory for Product Only models when themes are available.
    if (isProductOnlyModel() && options?.themes?.length > 0 && !generationParams.theme) {
      reasons.push('missing_theme');
    }

    return reasons;
  };

  // Check if user can generate (has credits, subscription active, and all required fields selected).
  const canGenerate = () => getGenerateBlockingReasons().length === 0;

  const getCreditDeficit = () => {
    const balance = merchant?.credits_balance || 0;
    const cost = dynamicCredits?.total_cost > 0 ? dynamicCredits.total_cost : calculateCredits();
    return Math.max(0, cost - balance);
  };



  const isVideoFormat = () => {
    if (!options || !generationParams.output_format) return false;
    const format = options.output_formats?.find(of => of.id === parseInt(generationParams.output_format));
    return format?.name === 'VIDEO';
  };

  // Helper function to get output format name for file extensions
  const getOutputFormatName = () => {
    if (!options || !generationParams.output_format) return 'jpg';
    const format = options.output_formats?.find(of => of.id === parseInt(generationParams.output_format));
    return format?.name?.toLowerCase() || 'jpg';
  };

  // Clear selections that fall outside plan-allowed / mapped options after options reload
  useEffect(() => {
    if (!options) return;

    setGenerationParams((prev) => {
      const next = { ...prev };
      let changed = false;

      const idStillAllowed = (selectedId, list) =>
        !selectedId || (list || []).some((item) => String(item.id) === String(selectedId));

      if (!idStillAllowed(prev.category, options.categories)) {
        next.category = '';
        changed = true;
      }
      if (!idStillAllowed(prev.model_type, options.model_types)) {
        next.model_type = '';
        changed = true;
      }
      if (!idStillAllowed(prev.output_format, options.output_formats)) {
        next.output_format = '';
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [options]);

  useEffect(() => {
    if (!options || !generationParams.model_type) return;

    setGenerationParams((prev) => {
      const next = { ...prev };
      let changed = false;

      const clearIfNotAllowed = (field, optionType) => {
        if (!prev[field]) return;
        const allowed = getFilteredOptions(optionType);
        if (!allowed.some((o) => String(o.id) === String(prev[field]))) {
          next[field] = '';
          changed = true;
        }
      };

      clearIfNotAllowed('ethnicity', 'ethnicities');
      clearIfNotAllowed('clothing_type', 'clothing_types');
      clearIfNotAllowed('bust_finish', 'bust_finishes');
      clearIfNotAllowed('bust_color', 'bust_colors');

      return changed ? next : prev;
    });
  }, [
    options,
    generationParams.category,
    generationParams.model_type,
    generationParams.gender_option_id,
    generationParams.bust_finish,
  ]);

  useEffect(() => {
    if (!isChainMaleSelection()) return;
    if (!generationParams.clothing_type) return;
    const allowed = new Set(['casual', 'formal']);
    const selected = options?.clothing_types?.find(ct => ct.id === parseInt(generationParams.clothing_type));
    const selectedName = (selected?.display_name || selected?.name || '').toLowerCase();
    if (!allowed.has(selectedName)) {
      setGenerationParams(prev => ({ ...prev, clothing_type: '' }));
    }
  }, [
    generationParams.category,
    generationParams.gender_option_id,
    generationParams.clothing_type,
    options
  ]);

  // Customer-photo flow does not support bangle / chain / ring / earring; clear selection if user switches tab with one of those selected.
  useEffect(() => {
    if (activePhotoTab !== 'customer' || !options?.categories?.length) return;
    const cat = options.categories.find((c) => String(c.id) === String(generationParams.category));
    if (!cat || !isCategoryHiddenForCustomerPhotoTab(cat)) return;
    const humanId = findHumanModelTypeId(options.model_types);
    setGenerationParams((prev) => ({
      ...prev,
      category: '',
      model_type: humanId || '',
      bust_color: '',
      ethnicity: '',
      clothing_type: '',
      bust_finish: '',
      gender_option_id: getFemaleOptionId(),
      selected_ai_model: '',
    }));
    setFieldErrors((prev) => ({ ...prev, category: '' }));
    loadOptions();
  }, [activePhotoTab, generationParams.category, options?.categories, options?.model_types]);

  // Customer tab: always send Human Model to the API while the model-type radios stay hidden.
  useEffect(() => {
    if (activePhotoTab !== 'customer' || !options?.model_types?.length) return;
    const humanId = findHumanModelTypeId(options.model_types);
    if (!humanId) return;
    setGenerationParams((prev) => {
      if (String(prev.model_type) === String(humanId)) return prev;
      return { ...prev, model_type: humanId };
    });
    setFieldErrors((prev) => ({ ...prev, model_type: '' }));
  }, [activePhotoTab, options?.model_types]);

  const getCreditBreakdownDisplay = () => {
    if (!creditBreakdown || Object.keys(creditBreakdown).length === 0) {
      return null;
    }

    const breakdown = [];
    // Base cost
    if (creditBreakdown.base_cost > 0) {
      breakdown.push(`Base: ${creditBreakdown.base_cost} ${FlutterServicesAPI.getCreditLabel().toLowerCase()}`);
    }

    // Watermark cost - only show when checkbox is checked (watermark enabled) AND no brand watermark selected
    if (generationParams.watermark && !generationParams.brand_watermark && creditBreakdown.watermark_cost < 0) {
      breakdown.push(`Watermark: ${creditBreakdown.watermark_cost} ${FlutterServicesAPI.getCreditLabel().toLowerCase()} (deduction)`);
    }

    // Brand logo cost removed - brand watermark is now free
    // if (generationParams.brand_watermark && creditBreakdown.brand_logo_cost > 0) {
    //   breakdown.push(`Brand Logo: +${creditBreakdown.brand_logo_cost} ${FlutterServicesAPI.getCreditLabel().toLowerCase()}`);
    // }

    // Multipliers - DISABLED
    // const multipliers = [];
    // if (creditBreakdown.model_type_multiplier !== 1.0) {
    //   multipliers.push(`Model: ${creditBreakdown.model_type_multiplier}x`);
    // }

    // if (creditBreakdown.category_multiplier !== 1.0) {
    //   multipliers.push(`Category: ${creditBreakdown.category_multiplier}x`);
    // }
    // if (creditBreakdown.material_multiplier !== 1.0) {
    //   multipliers.push(`Material: ${creditBreakdown.material_multiplier}x`);
    // }
    // if (creditBreakdown.quality_multiplier !== 1.0) {
    //   multipliers.push(`Output Quality: ${creditBreakdown.quality_multiplier}x`);
    // }
    // 
    // if (multipliers.length > 0) {
    //   breakdown.push(`Multipliers: ${multipliers.join(', ')}`);
    // }
    return breakdown;
  };

  const updateProgress = (generationId, newProgress) => {
    setBatchProgress(prev => ({
      ...prev,
      statuses: {
        ...prev.statuses,
        [generationId]: {
          ...prev.statuses[generationId],
          progress: newProgress
        }
      }
    }));
  };

  const handleGenerate = async () => {
    // Draft should only exist for "Generate -> Model Studio -> back" scenario.
    // Once user submits generation, clear any studio-return draft.
    clearModelStudioReturnDraft();

    // Check if subscription is suspended
    if (isSubscriptionSuspended()) {
      setError(getSubscriptionStatusMessage().message);
      return;
    }

    if (uploadedFiles.length === 0) {
      setError('Please upload at least one product image first');
      return;
    }

    if (activePhotoTab === 'customer' && !customerFile) {
      setError('Please upload a customer photo');
      return;
    }

    // Validate required fields
    if (!validateRequiredFields()) {
      return;
    }



    // Brand watermark validation is now handled in validateRequiredFields()

    // Check credit balance before starting generation
    const totalCreditsNeeded = calculateCredits();
    const currentBalance = merchant?.credits_balance || 0;

    if (totalCreditsNeeded > currentBalance) {
      const maxImagesPossible = calculateMaxImagesPossible();

      let errorMessage = `Insufficient ${FlutterServicesAPI.getCreditLabel().toLowerCase()} balance. You need ${formatCredits(totalCreditsNeeded)} ${FlutterServicesAPI.getCreditLabel().toLowerCase()} but only have ${formatCredits(currentBalance)} ${FlutterServicesAPI.getCreditLabel().toLowerCase()} available.`;

      if (maxImagesPossible > 0) {
        errorMessage += `\n\n💡 Tip: You can process ${maxImagesPossible} image${maxImagesPossible > 1 ? 's' : ''} with your current balance. Please remove ${uploadedFiles.length - maxImagesPossible} image${uploadedFiles.length - maxImagesPossible > 1 ? 's' : ''} to proceed.`;
      } else {
        errorMessage += `\n\n💡 Tip: Please request more ${FlutterServicesAPI.getCreditLabel().toLowerCase()} or remove some images to reduce the total cost.`;
      }
      setError(errorMessage);
      return;
    }

    // Show appropriate loader based on operation type
    const isBatchOperation = uploadedFiles.length > 1;
    const operationType = isVideoFormat() ? 'Video' : 'Image';
    const operationText = isBatchOperation ? `Batch ${operationType} Generation` : `${operationType} Generation`;

    setProcessingStatus({
      id: null,
      status: {
        name: 'PENDING',
        display_name: isBatchOperation ? `Initializing ${operationText}` : 'Initializing'
      }
    });
    setError('');
    setSuccessMessage('');
    setPollingCancelled(false); // Reset polling flag for new generation
    setPollingTimeout(null); // Clear any existing timeout



    try {
      // 📤 Upload customer photo if provided (any tab)
      let customerFaceImagePath = null;
      if (customerFile) {
        console.log("📤 Uploading customer photo...");
        const customerFormData = new FormData();
        customerFormData.append('file', customerFile);
        customerFormData.append('upload_type', 'customer');
        const customerUploadResponse = await aiGenerationAPI.uploadImage(customerFormData);
        customerFaceImagePath = customerUploadResponse.data?.file_path;
        console.log("✅ Customer photo uploaded:", customerFaceImagePath);
        if (!customerFaceImagePath) {
          throw new Error('Failed to upload customer photo');
        }
      }

      // Use selected brand logo if brand watermark is selected
      let brandLogoId = null;
      if (generationParams.brand_watermark && selectedBrandLogoId) {
        brandLogoId = parseInt(selectedBrandLogoId);
      }



      // Calculate credit cost per image
      const creditCostPerImage = calculateCredits() / uploadedFiles.length;

      // Generate batch ID for multiple images
      const batchId = uploadedFiles.length > 1 ? `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : null;

      // Check if selected category has gender mappings — gender_option_id is relevant for categories with gender options
      const selectedCategoryObj = options?.categories?.find(c => c.id === parseInt(generationParams.category));
      const selectedCategoryId = selectedCategoryObj?.id?.toString();
      const categoryGenderMappings = options?.category_gender_visibility || {};
      const hasGenderMappings = selectedCategoryId && categoryGenderMappings[selectedCategoryId] && categoryGenderMappings[selectedCategoryId].length > 0;

      // Build a backend-safe per-image caption list before request payloads.
      // When captionsList is still empty (user did not reopen Add Caption), fall back to generationParams
      // so regenerate URL prefill still reaches the backend.
      const sanitizedCaptions = generationParams.add_caption
        ? uploadedFiles.map((_, index) => {
          const captionItem = captionsList.find(c => c.imageIndex === index) || captionsList[index] || {};
          let text_1 = (captionItem.text_1 ?? captionItem.text ?? '').trim();
          let text_2 = (captionItem.text_2 ?? '').trim();
          if (index === 0) {
            if (!text_1) text_1 = (generationParams.caption_text_1 || '').trim();
            if (!text_2) text_2 = (generationParams.caption_text_2 || '').trim();
          }
          return { text_1, text_2 };
        })
        : [];

      const fallbackCaptionText1 = generationParams.add_caption ? ((sanitizedCaptions[0]?.text_1 || '').trim() || null) : null;
      const fallbackCaptionText2 = generationParams.add_caption ? ((sanitizedCaptions[0]?.text_2 || '').trim() || null) : null;

      // Catalog model row ethnicity when merchant picked an AI model.
      const ethnicityIdFromSelectedModel =
        !generationParams.selected_ai_model
          ? null
          : (() => {
            const mid = String(generationParams.selected_ai_model);
            const row = assignedAiModels.find((m) => String(m?.id) === mid);
            const eid = row?.ethnicity_id;
            return eid !== undefined && eid !== null && eid !== '' ? eid : null;
          })();
      // Customer photo flow hides ethnicity selector, so force ethnicity_id to null.
      const ethnicityIdForGenerateRequest = activePhotoTab === 'customer'
        ? null
        : (
          generationParams.selected_ai_model
            ? ethnicityIdFromSelectedModel
            : (generationParams.ethnicity || null)
        );

      // Check if this is batch processing (multiple images)
      const isBatchProcessing = uploadedFiles.length > 1;
      const isBatchVideoGeneration = isBatchProcessing && isVideoFormat();
      const isBatchImageGeneration = isBatchProcessing && !isVideoFormat();

      if (isBatchVideoGeneration) {

        // Upload all images first
        console.log(`📤 Starting upload of ${uploadedFiles.length} images...`);
        const uploadPromises = uploadedFiles.map(async (file, index) => {
          console.log(`📤 Uploading file ${index + 1}/${uploadedFiles.length}: ${file.name}`);
          const formData = new FormData();
          formData.append('file', file);
          const uploadResponse = await aiGenerationAPI.uploadImage(formData);
          console.log(`✅ Upload response for ${file.name}:`, uploadResponse.data);
          return uploadResponse.data?.file_path;
        });

        const imagePaths = await Promise.all(uploadPromises);
        console.log(`📋 All image paths received:`, imagePaths);

        // Check for upload failures
        const failedUploads = imagePaths.filter(path => !path);
        if (failedUploads.length > 0) {
          console.error(`❌ Upload failures:`, failedUploads);
          throw new Error(`Failed to upload ${failedUploads.length} images`);
        }

        console.log(`✅ All ${imagePaths.length} images uploaded successfully`);

        // Prepare batch video generation data
        const batchVideoData = {
          image_paths: imagePaths,
          model_type_id: generationParams.model_type,
          output_format_id: parseInt(generationParams.output_format),
          category_id: generationParams.category || null,
          bust_color_id: generationParams.bust_color || null,
          ethnicity_id: ethnicityIdForGenerateRequest,
          // Only send model_face_image_id when the user is NOT on customer tab and has selected a model
          model_face_image_id: activePhotoTab === 'customer' ? null : (generationParams.selected_ai_model ? parseInt(generationParams.selected_ai_model, 10) : null),
          customer_face_image_path: customerFaceImagePath,
          clothing_type_id: generationParams.clothing_type || null,
          bust_finish_id: generationParams.bust_finish || null,
          has_watermark: generationParams.watermark,
          has_brand_logo: generationParams.brand_watermark,
          watermark_type: (generationParams.brand_watermark && generationParams.watermark) ? 'BOTH' :
            generationParams.brand_watermark ? 'BRAND' :
              generationParams.watermark ? 'GLAMMIFY' : 'NONE',
          brand_logo_id: brandLogoId,
          output_quality: generationParams.output_quality || 'STANDARD',
          caption_text_1: fallbackCaptionText1,
          caption_text_2: fallbackCaptionText2,
          captions: sanitizedCaptions,
          material_id: generationParams.material || null,
          media_quality_id: generationParams.media_quality_id,
          custom_tags: generationParams.custom_tags.join(','),
          theme_id: generationParams.theme || null
        };

        // Only include gender_option_id for categories with gender mappings
        if (hasGenderMappings && generationParams.gender_option_id) {
          batchVideoData.gender_option_id = generationParams.gender_option_id;
        }

        console.log(`📡 BATCH VIDEO DATA TO BE SENT:`, JSON.stringify(batchVideoData, null, 2));

        // Call batch video generation API
        const batchResponse = await aiGenerationAPI.startBatchVideoGeneration(batchVideoData);

        const generationIds = batchResponse.data?.generation_ids || [];
        const batchId = batchResponse.data?.batch_id;

        if (generationIds.length === 0) {
          throw new Error('Failed to start batch video generation - no generation IDs returned');
        }

        // Create generation results from batch response
        const generationResults = generationIds.map((id, index) => ({
          id: id,
          fileName: uploadedFiles[index].name,
          status: 'processing'
        }));

        // Initialize batch progress tracking
        setBatchProgress({
          completed: 0,
          total: generationResults.length,
          statuses: generationResults.reduce((acc, gen) => {
            acc[gen.id] = { status: 'PENDING', fileName: gen.fileName };
            return acc;
          }, {}),
          batchId: batchId
        });

        setGenerationResult({
          id: generationResults[0].id,
          status: 'processing',
          allGenerations: generationResults,
          batchId: batchId
        });

        setProcessingStatus({
          id: generationResults[0].id,
          status: {
            name: 'PENDING',
            display_name: 'Initializing Batch Video Generation'
          },
          allGenerations: generationResults,
          batchId: batchId
        });

        console.log(`🔄 Batch video generation ID for tracking: ${batchId}`);

        // Start batch status monitoring
        startBatchStatusMonitoring(generationResults, batchId);

        return; // Exit early for batch video generation
      }

      if (isBatchImageGeneration) {

        // Upload all images first
        const uploadPromises = uploadedFiles.map(async (file, index) => {
          const formData = new FormData();
          formData.append('file', file);
          const uploadResponse = await aiGenerationAPI.uploadImage(formData);
          return uploadResponse.data?.file_path;
        });

        const imagePaths = await Promise.all(uploadPromises);

        // Check for upload failures
        const failedUploads = imagePaths.filter(path => !path);
        if (failedUploads.length > 0) {
          throw new Error(`Failed to upload ${failedUploads.length} images`);
        }

        // Prepare batch image generation data
        const batchImageData = {
          image_paths: imagePaths,
          model_type_id: generationParams.model_type,
          output_format_id: parseInt(generationParams.output_format),
          category_id: generationParams.category || null,
          bust_color_id: generationParams.bust_color || null,
          ethnicity_id: ethnicityIdForGenerateRequest,
          // Only send model_face_image_id when the user is NOT on customer tab and has selected a model
          model_face_image_id: activePhotoTab === 'customer' ? null : (generationParams.selected_ai_model ? parseInt(generationParams.selected_ai_model, 10) : null),
          customer_face_image_path: customerFaceImagePath,
          clothing_type_id: generationParams.clothing_type || null,
          bust_finish_id: generationParams.bust_finish || null,
          has_watermark: generationParams.watermark,
          has_brand_logo: generationParams.brand_watermark,
          watermark_type: (generationParams.brand_watermark && generationParams.watermark) ? 'BOTH' :
            generationParams.brand_watermark ? 'BRAND' :
              generationParams.watermark ? 'GLAMMIFY' : 'NONE',
          brand_logo_id: brandLogoId,
          output_quality: generationParams.output_quality || 'STANDARD',
          caption_text_1: fallbackCaptionText1,
          caption_text_2: fallbackCaptionText2,
          captions: sanitizedCaptions,
          material_id: generationParams.material || null,
          media_quality_id: generationParams.media_quality_id,
          custom_tags: generationParams.custom_tags.join(','),
          theme_id: generationParams.theme || null
        };

        // Only include gender_option_id for categories with gender mappings
        if (hasGenderMappings && generationParams.gender_option_id) {
          batchImageData.gender_option_id = generationParams.gender_option_id;
        }

        console.log(`📡 BATCH IMAGE DATA TO BE SENT:`, JSON.stringify(batchImageData, null, 2));

        // Call batch image generation API
        console.log(`📡 Calling batch image generation API...`);
        const batchResponse = await aiGenerationAPI.startBatchImageGeneration(batchImageData);
        console.log(`📡 Batch API response:`, batchResponse);

        const generationIds = batchResponse.data?.generation_ids || [];
        const batchId = batchResponse.data?.batch_id;

        if (generationIds.length === 0) {
          throw new Error('Failed to start batch image generation - no generation IDs returned');
        }

        // Create generation results from batch response
        const generationResults = generationIds.map((id, index) => ({
          id: id,
          fileName: uploadedFiles[index].name,
          status: 'processing'
        }));

        // Initialize batch progress tracking
        setBatchProgress({
          completed: 0,
          total: generationResults.length,
          statuses: generationResults.reduce((acc, gen) => {
            acc[gen.id] = { status: 'PENDING', fileName: gen.fileName };
            return acc;
          }, {}),
          batchId: batchId
        });

        setGenerationResult({
          id: generationResults[0].id,
          status: 'processing',
          allGenerations: generationResults,
          batchId: batchId
        });

        setProcessingStatus({
          id: generationResults[0].id,
          status: {
            name: 'PENDING',
            display_name: 'Initializing Batch Image Generation'
          },
          allGenerations: generationResults,
          batchId: batchId
        });

        console.log(`🔄 Batch image generation ID for tracking: ${batchId}`);

        // Start batch status monitoring
        startBatchStatusMonitoring(generationResults, batchId);

        return; // Exit early for batch image generation
      }

      // Single image processing (existing logic)

      const generationPromises = uploadedFiles.map(async (file, index) => {
        // Upload the image
        const formData = new FormData();
        formData.append('file', file);

        const uploadResponse = await aiGenerationAPI.uploadImage(formData);
        const filePath = uploadResponse.data?.file_path;

        if (!filePath) {
          throw new Error(`Failed to upload image ${file.name} - invalid response from server`);
        }

        // Start generation
        const generationData = {
          original_image_path: filePath,
          model_type_id: generationParams.model_type,
          output_format_id: parseInt(generationParams.output_format), // No default - user must select
          category_id: generationParams.category || null,
          bust_color_id: generationParams.bust_color || null,

          ethnicity_id: ethnicityIdForGenerateRequest,
          // Only send model_face_image_id when the user is NOT on customer tab and has selected a model
          model_face_image_id: activePhotoTab === 'customer' ? null : (generationParams.selected_ai_model ? parseInt(generationParams.selected_ai_model, 10) : null),
          customer_face_image_path: customerFaceImagePath,
          clothing_type_id: generationParams.clothing_type || null,
          bust_finish_id: generationParams.bust_finish || null,
          has_watermark: generationParams.watermark, // Only true when "Add Glammify Watermark" is checked (no extra cost)
          has_brand_logo: generationParams.brand_watermark, // Only true when brand watermark is selected
          watermark_type: (generationParams.brand_watermark && generationParams.watermark) ? 'BOTH' :
            generationParams.brand_watermark ? 'BRAND' :
              generationParams.watermark ? 'GLAMMIFY' : 'NONE',
          brand_logo_id: brandLogoId,
          caption_text_1: generationParams.add_caption
            ? ((sanitizedCaptions[index]?.text_1 || fallbackCaptionText1 || '').trim() || null)
            : null,
          caption_text_2: generationParams.add_caption
            ? ((sanitizedCaptions[index]?.text_2 || fallbackCaptionText2 || '').trim() || null)
            : null,
          media_quality_id: generationParams.media_quality_id,
          custom_tags: generationParams.custom_tags.join(','),
          theme_id: generationParams.theme || null,
          credit_cost: creditCostPerImage,

          batch_id: batchId  // Add batch ID for tracking
        };

        // Only include gender_option_id for categories with gender mappings
        if (hasGenderMappings && generationParams.gender_option_id) {
          generationData.gender_option_id = generationParams.gender_option_id;
        }

        console.log(` Sending generation data for file ${index + 1}:`, generationData);
        console.log(`📡 SINGLE FILE DATA TO BE SENT:`, JSON.stringify(generationData, null, 2));

        // Call appropriate API based on output format
        let generationResponse;
        if (isVideoFormat()) {
          generationResponse = await aiGenerationAPI.startVideoGeneration(generationData);
        } else {
          generationResponse = await aiGenerationAPI.startImageGeneration(generationData);
        }
        const generationId = generationResponse.data?.generation_id;

        if (!generationId) {
          throw new Error(`Failed to start generation for ${file.name} - invalid response from server`);
        }
        return { id: generationId, fileName: file.name, status: 'processing' };
      });

      const generationResults = await Promise.all(generationPromises);

      // Initialize batch progress tracking
      setBatchProgress({
        completed: 0,
        total: generationResults.length,
        statuses: generationResults.reduce((acc, gen) => {
          acc[gen.id] = { status: 'PENDING', fileName: gen.fileName };
          return acc;
        }, {}),
        batchId: batchId  // Store batch ID for tracking
      });

      setGenerationResult({
        id: generationResults[0].id, // Use first generation ID for backward compatibility
        status: 'processing',
        allGenerations: generationResults,
        batchId: batchId  // Store batch ID
      });
      // Update processing status with generation ID
      const isBatchOperation = uploadedFiles.length > 1;
      const operationType = isVideoFormat() ? 'Video' : 'Image';
      const operationText = isBatchOperation ? `Batch ${operationType} Generation` : `${operationType} Generation`;
      // Initialize batch progress for both single and batch operations
      setBatchProgress({
        completed: 0,
        total: generationResults.length,
        statuses: generationResults.reduce((acc, gen) => {
          acc[gen.id] = {
            status: 'PENDING',
            fileName: gen.fileName || `Generation ${gen.id}`,
            completedAt: null,
            failedAt: null
          };
          return acc;
        }, {}),
        batchId: batchId,
        failed: 0
      });

      setProcessingStatus({
        id: generationResults[0].id,
        status: {
          name: 'PENDING',
          display_name: isBatchOperation ? `Initializing ${operationText}` : 'Initializing'
        },
        allGenerations: generationResults,
        batchId: batchId  // Store batch ID
      });



      // For single image, use individual polling; for batch, use batch monitoring
      if (uploadedFiles.length === 1) {
        pollGenerationStatus(generationResults[0].id, 0);
      } else {
        startBatchStatusMonitoring(generationResults, batchId);
      }

    } catch (error) {
      console.error('Generation failed:', error);

      // Handle specific timeout errors
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        console.warn('⚠️ Timeout detected - generation may still be processing');

        // If we have a generation ID from the response, continue polling
        if (generationResult && generationResult.id) {
          await handleTimeoutAndContinuePolling(generationResult.id, uploadedFiles[0]?.name || 'Unknown file');
        } else {
          // No generation ID, show error message
          const errorMessage = 'Generation request timed out. The generation may still be processing in the background. Please check the history tab for updates.';
          setError(errorMessage);

          // Show a more helpful message to the user
          setSuccessMessage('Generation request timed out, but your image may still be processing. Check the history tab for updates.');

          // Clear the error after 5 seconds to show the success message
          setTimeout(() => {
            setError('');
          }, 5000);
        }
      } else if (error.response?.data?.code?.startsWith('VERIFICATION_')) {
        // Insufficient tokens while the account awaits (or was refused) verification
        setError(error.response.data.message);
      } else {
        setError('We are upgrading the system. We will be back shortly.');
        console.log('Error:', error);
      }

      setProcessingStatus(null);
    }
  };

  // Send mobile notification for successful generation
  const sendMobileNotification = async (generation) => {
    try {
      // Check if generation object is valid
      if (!generation || !generation.id) {
        console.log('📱 Invalid generation object, skipping mobile notification');
        return;
      }

      // Check if merchant is available
      if (!merchant || !merchant.id) {
        console.log('📱 No merchant data available, skipping mobile notification');
        return;
      }

      // Check notification settings - RESPECT USER PREFERENCES
      if (!merchant.notification_settings?.generation_complete) {
        console.log('🔕 Generation complete notifications disabled by user preference, skipping mobile notification');
        return;
      }

      console.log('🔔 Generation complete notifications enabled, proceeding with mobile notification');

      // Only send notification if running in Flutter app (mobile)
      if (FlutterServicesAPI.isFlutterApp()) {
        console.log('📱 Sending mobile notification for completed generation:', generation.id);
        // Prepare notification data
        const notificationData = {
          merchant_id: merchant.id,
          title: "🎨 AI Generation Complete!",
          body: "Your AI-generated content is ready to view and download.",
          data: {
            type: 'ai_generation_complete',
            generation_id: generation.id,
            image_url: generation.generated_image_path || generation.generated_video_path,
            download_url: generation.generated_image_path || generation.generated_video_path,
            timestamp: new Date().toISOString()
          },
          image_url: generation.generated_image_path || generation.generated_video_path
        };

        // Send notification via FlutterServicesAPI
        const result = await FlutterServicesAPI.sendSingleNotification(notificationData);

        if (result.success) {
          console.log('✅ Mobile notification sent successfully');
        } else {
          console.warn('⚠️ Failed to send mobile notification:', result.message);
        }
      } else {
        console.log('📱 Not in Flutter app, skipping mobile notification');
      }
    } catch (error) {
      console.error('❌ Error sending mobile notification:', error);
      // Don't throw error to avoid breaking the generation flow
    }
  };

  // Handle timeout scenarios by continuing to poll for generation status
  const handleTimeoutAndContinuePolling = async (generationId, fileName) => {
    console.log(`⏰ Timeout detected for generation ${generationId}, continuing to poll for status...`);

    // Set a processing status to show the user that we're still monitoring
    setProcessingStatus({
      id: generationId,
      status: {
        name: 'PENDING',
        display_name: 'Generation in progress (checking status...)'
      },
      fileName: fileName
    });

    // Start polling for the generation status
    pollGenerationStatus(generationId, 0);

    // Show helpful message to user
    setSuccessMessage('Generation request timed out, but we\'re still monitoring the progress. You can check the history tab for updates.');

    // Clear the message after 8 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 8000);
  };

  // Send mobile notification for failed generation
  const sendMobileFailureNotification = async (generation) => {
    try {
      // Check if generation object is valid
      if (!generation || !generation.id) {
        console.log('📱 Invalid generation object, skipping mobile failure notification');
        return;
      }

      // Check if merchant is available
      if (!merchant || !merchant.id) {
        console.log('📱 No merchant data available, skipping mobile failure notification');
        return;
      }

      // Check notification settings - RESPECT USER PREFERENCES
      if (!merchant.notification_settings?.generation_complete) {
        console.log('🔕 Generation complete notifications disabled by user preference, skipping mobile failure notification');
        return;
      }

      console.log('🔔 Generation complete notifications enabled, proceeding with mobile failure notification');

      // Only send notification if running in Flutter app (mobile)
      if (FlutterServicesAPI.isFlutterApp()) {
        console.log('📱 Sending mobile failure notification for generation:', generation.id);
        // Prepare notification data
        const notificationData = {
          merchant_id: merchant.id,
          title: "⚠️ AI Generation Failed",
          body: "We encountered an issue with your AI generation. Please try again.",
          data: {
            type: 'ai_generation_failed',
            generation_id: generation.id,
            error: generation.error_message || 'Unknown error',
            timestamp: new Date().toISOString()
          }
        };

        // Send notification via FlutterServicesAPI
        const result = await FlutterServicesAPI.sendSingleNotification(notificationData);

        if (result.success) {
          console.log('✅ Mobile failure notification sent successfully');
        } else {
          console.warn('⚠️ Failed to send mobile failure notification:', result.message);
        }
      } else {
        console.log('📱 Not in Flutter app, skipping mobile failure notification');
      }
    } catch (error) {
      console.error('❌ Error sending mobile failure notification:', error);
      // Don't throw error to avoid breaking the generation flow
    }
  };

  const pollGenerationStatus = async (generationId, retryCount = 0) => {
    // Maximum retries to prevent infinite polling (20 minutes with 3-second intervals)
    const MAX_RETRIES = 400;
    if (retryCount >= MAX_RETRIES) {
      setProcessingStatus(null);
      setError('Generation is taking longer than expected. Please check the history tab for updates.');
      return;
    }

    try {
      const response = await aiGenerationAPI.getStatus(generationId);

      // Handle different possible response structures
      let generation = null;

      if (response.data?.status) {
        // Backend returns: { "status": { generation object } }
        generation = response.data.status;
      } else if (response.data?.generation) {
        // Alternative structure: { "generation": { generation object } }
        generation = response.data.generation;
      } else if (response.data?.id) {
        // If the response is the generation object directly
        generation = response.data;
      }
      if (!generation) {
        // Instead of showing error, continue polling as the generation might still be processing
        setTimeout(() => pollGenerationStatus(generationId, retryCount + 1), 3000);
        return;
      }

      // Check if generation is completed
      const isCompleted = generation.status && generation.status.name === 'COMPLETED';

      // Update batch progress for single image processing
      const currentScrollY = window.scrollY;
      setBatchProgress(prev => {
        const newStatuses = { ...prev.statuses };
        const currentProgress = prev.statuses[generationId]?.progress || 10;

        // Simulate progress increments for processing status
        let newProgress = currentProgress;
        if (generation.status?.name === 'PROCESSING') {
          // Increment progress by 10-20% each poll, but don't exceed 90%
          newProgress = Math.min(currentProgress + Math.floor(Math.random() * 20) + 10, 90);
        } else if (generation.status?.name === 'COMPLETED') {
          newProgress = 100;
        } else if (generation.status?.name === 'FAILED') {
          newProgress = 0;
        }

        newStatuses[generationId] = {
          status: generation.status?.name || 'UNKNOWN',
          fileName: prev.statuses[generationId]?.fileName || `Generation ${generationId}`,
          completedAt: generation.status?.name === 'COMPLETED' ? new Date().toLocaleTimeString() : null,
          failedAt: generation.status?.name === 'FAILED' ? new Date().toLocaleTimeString() : null,
          progress: newProgress
        };

        let completed = 0;
        let failed = 0;
        Object.values(newStatuses).forEach(status => {
          if (status.status === 'COMPLETED') completed++;
          else if (status.status === 'FAILED') failed++;
        });

        return {
          ...prev,
          completed,
          failed,
          statuses: newStatuses
        };
      });

      // Restore scroll position after state update
      requestAnimationFrame(() => {
        if (window.scrollY !== currentScrollY) {
          window.scrollTo(0, currentScrollY);
        }
      });

      if (isCompleted) {
        setProcessingStatus(null); // Clear processing status
        setGenerationResult(prev => ({ ...prev, ...generation }));

        // Send mobile notification for successful generation
        await sendMobileNotification(generation);

        // Refresh merchant profile to update credit balance
        await refreshProfile();

        // Show preview dialog instead of direct navigation
        setTimeout(() => {
          setCompletionGeneration(generation);
          setShowPreviewDialog(true);
        }, 500);

      } else if (generation.status && generation.status.name === 'FAILED') {
        setProcessingStatus(null); // Clear processing status
        setError('We are upgrading the system. We will be back shortly.');
        console.log('Generation STATUS:', generation.status);
        console.log('Generation ERROR NAME:', generation.error_message);

        // Send mobile notification for failed generation
        await sendMobileFailureNotification(generation);

        // Refresh merchant profile to update credit balance even if generation failed
        await refreshProfile();
      } else if (generation.status && (generation.status.name === 'PENDING' || generation.status.name === 'PROCESSING')) {
        // Continue polling for pending or processing status
        setTimeout(() => pollGenerationStatus(generationId, retryCount + 1), 3000);
      } else {
        // Handle any other status by continuing to poll
        setTimeout(() => pollGenerationStatus(generationId, retryCount + 1), 3000);
      }
    } catch (error) {
      // Don't clear processing status on error, continue polling
      setTimeout(() => pollGenerationStatus(generationId, retryCount + 1), 3000);
    }
  };

  // Enhanced batch status monitoring for multiple generations
  const startBatchStatusMonitoring = async (generationResults, batchId) => {
    const MAX_RETRIES = 400;
    const POLLING_INTERVAL = 3000; // 3 seconds
    const monitorBatch = async (retryCount = 0) => {
      if (retryCount >= MAX_RETRIES || pollingCancelled) {
        return;
      }

      try {
        const generationIds = generationResults.map(gen => gen.id);

        // Try batch status API first
        try {
          const response = await aiGenerationAPI.getBatchStatus(generationIds);
          const batchData = response.data.generations || [];
          // Update batch progress
          const currentScrollY = window.scrollY;
          setBatchProgress(prev => {
            const newStatuses = { ...prev.statuses };
            let completed = 0;
            let processing = 0;
            let failed = 0;

            batchData.forEach(gen => {
              const status = gen.status?.name || 'UNKNOWN';
              newStatuses[gen.id] = {
                status,
                fileName: generationResults.find(r => r.id === gen.id)?.fileName || `Generation ${gen.id}`,
                completedAt: status === 'COMPLETED' ? new Date().toLocaleTimeString() : null,
                failedAt: status === 'FAILED' ? new Date().toLocaleTimeString() : null
              };
              if (status === 'COMPLETED') {
                completed++;
              } else if (status === 'FAILED') {
                failed++;
              } else if (status === 'PROCESSING') {
                processing++;
              }
            });
            // Update processing status with batch progress
            const operationType = isVideoFormat() ? 'Video' : 'Image';
            const operationText = `Batch ${operationType} Generation`;
            let statusMessage = `Initializing ${operationText}`;
            if (completed > 0 && completed === prev.total) {
              statusMessage = `${operationText} - All ${completed} files completed!`;
            } else if (completed > 0) {
              statusMessage = `${operationText} - ${completed}/${prev.total} complete`;
            } else if (processing > 0) {
              statusMessage = `${operationText} - Processing ${processing} files`;
            } else if (failed > 0) {
              statusMessage = `${operationText} - ${failed} failed, ${completed} completed`;
            }
            setProcessingStatus(prevStatus => ({
              ...prevStatus,
              status: {
                name: 'PROCESSING',
                display_name: statusMessage
              }
            }));
            return {
              ...prev,
              completed,
              failed,
              statuses: newStatuses
            };
          });
          // Restore scroll position after state update
          requestAnimationFrame(() => {
            if (window.scrollY !== currentScrollY) {
              window.scrollTo(0, currentScrollY);
            }
          });

          // Check if all generations are complete
          const allCompleted = batchData.every(gen =>
            gen.status?.name === 'COMPLETED' || gen.status?.name === 'FAILED'
          );

          if (allCompleted && !isBatchCompleted) {
            setIsBatchCompleted(true);

            // Show completion celebration for 3 seconds before redirecting
            setProcessingStatus(prevStatus => ({
              ...prevStatus,
              status: {
                name: 'COMPLETED',
                display_name: `🎉 All ${batchProgress.total} files completed successfully!`
              }
            }));

            // Wait 3 seconds to show completion message, then redirect
            setTimeout(async () => {
              setProcessingStatus(null);
              await refreshProfile();
              try {
                navigate('/history');
                // Force a page refresh if navigation doesn't work after 2 seconds
                setTimeout(() => {
                  if (window.location.pathname !== '/history') {
                    window.location.href = '/history';
                  }
                }, 2000);
              } catch (navError) {
                // Fallback to direct navigation
                window.location.href = '/history';
              }
            }, 3000);
            return; // Stop further API calls
          } else if (!allCompleted) {
            // Continue monitoring if not all completed
            setTimeout(() => monitorBatch(retryCount + 1), POLLING_INTERVAL);
          }

        } catch (batchError) {

          // Fallback to individual status calls
          const individualPromises = generationIds.map(async (genId) => {
            try {
              const response = await aiGenerationAPI.getStatus(genId);
              let generation = null;
              if (response.data?.status) {
                generation = response.data.status;
              } else if (response.data?.generation) {
                generation = response.data.generation;
              } else if (response.data?.id) {
                generation = response.data;
              }
              return { id: genId, generation };
            } catch (error) {
              console.error(`Failed to get status for generation ${genId}:`, error);
              return { id: genId, generation: null };
            }
          });

          const individualResults = await Promise.all(individualPromises);

          // Update batch progress with individual results
          setBatchProgress(prev => {
            const newStatuses = { ...prev.statuses };
            let completed = 0;
            let processing = 0;

            individualResults.forEach(result => {
              if (result.generation) {
                const status = result.generation.status?.name || 'UNKNOWN';
                newStatuses[result.id] = {
                  status,
                  fileName: generationResults.find(r => r.id === result.id)?.fileName || `Generation ${result.id}`
                };

                if (status === 'COMPLETED' || status === 'FAILED') {
                  completed++;
                } else if (status === 'PROCESSING') {
                  processing++;
                }
              }
            });
            // Update processing status with batch progress
            const operationType = isVideoFormat() ? 'Video' : 'Image';
            const operationText = `Batch ${operationType} Generation`;
            let statusMessage = `Initializing ${operationText}`;
            if (completed > 0) {
              statusMessage = `${operationText} - ${completed}/${prev.total} complete`;
            } else if (processing > 0) {
              statusMessage = `${operationText} - Processing ${processing} files`;
            }
            setProcessingStatus(prevStatus => ({
              ...prevStatus,
              status: {
                name: 'PROCESSING',
                display_name: statusMessage
              }
            }));
            return {
              ...prev,
              completed,
              statuses: newStatuses
            };
          });

          // Check if all generations are complete
          const allCompleted = individualResults.every(result =>
            result.generation && (result.generation.status?.name === 'COMPLETED' || result.generation.status?.name === 'FAILED')
          );

          if (allCompleted) {
            // Show completion message for 3 seconds before redirecting
            setProcessingStatus(prevStatus => ({
              ...prevStatus,
              status: {
                name: 'COMPLETED',
                display_name: `🎉 Batch generation completed successfully!`
              }
            }));

            setTimeout(async () => {
              setProcessingStatus(null);
              await refreshProfile();
              try {
                navigate('/history');
              } catch (navError) {
                window.location.href = '/history';
              }
            }, 3000);
            return;
          } else {
            // Continue monitoring
            setTimeout(() => monitorBatch(retryCount + 1), POLLING_INTERVAL);
          }
        }
      } catch (error) {
        console.error('Failed to check batch status:', error);
        // Continue monitoring on error
        setTimeout(() => monitorBatch(retryCount + 1), POLLING_INTERVAL);
      }
    };
    // Start batch monitoring
    console.log(`🎯 Starting initial batch monitoring...`);
    monitorBatch(0);
  };

  const handleDownload = async () => {
    try {
      if (generationResult.allGenerations && generationResult.allGenerations.length > 1) {
        // Download all generated files
        const downloadPromises = generationResult.allGenerations.map(async (gen, index) => {
          const response = await aiGenerationAPI.downloadGeneration(gen.id);
          const blob = new Blob([response.data]);
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = `glammify-${gen.id}-${gen.fileName || `image-${index + 1}`}.${getOutputFormatName()}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
        });
        await Promise.all(downloadPromises);
      } else {
        // Download single file (backward compatibility)
        const response = await aiGenerationAPI.downloadGeneration(generationResult.id);
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `glammify-${generationResult.id}.${getOutputFormatName()}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }
      // Open feedback dialog
      setFeedbackDialog(true);
    } catch (error) {
      console.error('Download failed:', error);
      setError('Download failed');
    }
  };

  const handleFeedbackSubmit = async () => {
    try {
      await aiGenerationAPI.submitFeedback(generationResult.id, feedback);
      setFeedbackDialog(false);
      navigate('/dashboard');
    } catch (error) {
      console.error('Feedback submission failed:', error);
    }
  };

  // Show processing status if generation is in progress
  const filteredModelTypes = useMemo(() => {
    if (!options?.model_types || !generationParams.category) {
      return options?.model_types || [];
    }

    const selectedCategory = options.categories?.find(c => c.id === parseInt(generationParams.category));
    if (!selectedCategory) {
      return options.model_types;
    }

    // Use backend mapping if available
    const mapping = options.category_model_mappings?.[selectedCategory.name];

    // If mapping exists, filter model types
    if (mapping && Array.isArray(mapping)) {
      console.log(`🔍 Filtering model types for ${selectedCategory.name}:`, mapping);
      return options.model_types.filter(mt => mapping.includes(mt.name) || mt.name === 'PRODUCT_ONLY');
    }

    // If no mapping exists for this category, show all model types
    return options.model_types;
  }, [options, generationParams.category]);

  // Human Hand flow does not use AI model face / ethnicity selectors.
  const isHumanHandSelected = useMemo(() => {
    if (!generationParams.model_type || !options?.model_types?.length) return false;
    const selected = options.model_types.find(
      (mt) => String(mt.id) === String(generationParams.model_type)
    );
    if (!selected) return false;
    const raw = `${selected.name || ''} ${selected.display_name || ''}`
      .toLowerCase()
      .replace(/[_-]/g, ' ')
      .trim();
    return raw.includes('human hand');
  }, [generationParams.model_type, options?.model_types]);

  // Bust flow does not use the assigned AI model selector.
  const isBustSelected = useMemo(() => {
    if (!generationParams.model_type || !options?.model_types?.length) return false;
    const selected = options.model_types.find(
      (mt) => String(mt.id) === String(generationParams.model_type)
    );
    if (!selected) return false;
    const raw = `${selected.name || ''} ${selected.display_name || ''}`
      .toLowerCase()
      .replace(/[_-]/g, ' ')
      .trim();
    return raw.includes('bust');
  }, [generationParams.model_type, options?.model_types]);

  // Allow ethnicity only for the specific case: Female + Ring (even if model type is Human Hand).
  const allowEthnicityForFemaleRing = useMemo(() => {
    if (!options?.categories?.length) return false;
    const selectedCategory = options.categories.find(
      (c) => String(c.id) === String(generationParams.category)
    );
    const slug = (selectedCategory?.name || selectedCategory?.display_name || '')
      .toLowerCase()
      .trim();
    const isRing = slug === 'ring' || slug === 'rings';

    const femaleId = getFemaleOptionId(options);
    const isFemale =
      !!femaleId && String(generationParams.gender_option_id) === String(femaleId);

    return isRing && isFemale;
  }, [options, generationParams.category, generationParams.gender_option_id]);


  if (processingStatus) {
    return (
      <Box>
        {/* <MerchantWelcomeSection /> */}
        <DynamicMerchantTabs />

        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{
            py: { xs: 1, md: 1 },
            px: 0,
            textAlign: 'center',
            borderRadius: 0,
            background: 'radial-gradient(ellipse at 15% 20%, rgba(98,24,197,.05) 0%, transparent 55%),radial-gradient(ellipse at 85% 80%, rgba(139,92,246,.06) 0%, transparent 55%),var(--soft-gray)',
            border: 'none',
            position: 'relative',
            overflow: 'hidden',
            minHeight: { xs: '85vh', md: '460px' },
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {/* Background Gradient Effects */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 20% 20%, rgba(147, 51, 238, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
              pointerEvents: 'none'
            }} />

            {/* AI Generation Animation */}
            <Box ref={loaderRef} sx={{ position: 'relative', zIndex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <GlammifyAnimationLoader />

              {/* Consolidated Progress Bar */}
              {/* {batchProgress.total > 0 && (
                <Box sx={{
                  position: 'relative',
                  zIndex: 1,
                  maxWidth: 500,
                  px: 3,
                  width: '100%',
                  mx: 'auto',
                  mt: 3
                }}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'rgba(147, 51, 238, 0.05)',
                    border: '1px solid rgba(147, 51, 238, 0.2)',
                    mb: 10
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{
                        fontWeight: 600,
                        color: '#6318C6'
                      }}>
                        Overall Progress
                      </Typography>
                      <Typography variant="body2" sx={{
                        fontWeight: 600,
                        color: '#6318C6'
                      }}>
                        {batchProgress.completed} of {batchProgress.total} completed
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(batchProgress.completed / batchProgress.total) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'rgba(147, 51, 238, 0.2)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#6318C6',
                            borderRadius: 4
                          }
                        }}
                      />
                    </Box>

                    <Typography variant="caption" sx={{
                      color: '#6b7280',
                      fontWeight: 500
                    }}>
                      {Math.round((batchProgress.completed / batchProgress.total) * 100)}% Complete
                    </Typography>
                  </Box>
                </Box>
              )} */}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // Guidelines Dialog Component
  const GuidelinesDialog = () => (
    <Dialog
      open={guidelinesOpen}
      onClose={() => setGuidelinesOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      <DialogTitle sx={{
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: '1px solid #e5e7eb'
      }}>
        <Help sx={{ color: '#6318C6', fontSize: { xs: '20px', sm: '24px' } }} />
        <Typography variant="h6" sx={{
          fontWeight: '600',
          color: '#1f2937',
          fontSize: { xs: '16px', sm: '18px', md: '20px' }
        }}>
          Image Upload Guidelines
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Supported Formats */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{
              fontWeight: '600',
              color: '#1f2937',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: { xs: '14px', sm: '16px' }
            }}>
              <ImageIcon sx={{ fontSize: { xs: '16px', sm: '20px' }, color: '#6318C6' }} />
              Supported Formats
            </Typography>
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1
            }}>
              {['PNG', 'JPG', 'JPEG', 'WebP', 'HEIC'].map((format) => (
                <Chip
                  key={format}
                  label={format}
                  size="small"
                  sx={{
                    backgroundColor: '#f3f0ff',
                    color: '#6318C6',
                    fontWeight: '600',
                    fontSize: { xs: '10px', sm: '12px' }
                  }}
                />
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
              Only these image formats are supported for AI generation.
            </Typography>
          </Box>

          {/* File Size */}
          <Box>
            <Typography variant="subtitle1" sx={{
              fontWeight: '600',
              color: '#1f2937',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: { xs: '14px', sm: '16px' }
            }}>
              <Warning sx={{ fontSize: { xs: '16px', sm: '20px' }, color: '#f59e0b' }} />
              File Size Limit
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '12px', sm: '14px' }, mb: 1 }}>
              Maximum file size: <strong>12MB per image</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
              Larger files will be automatically rejected to ensure optimal processing.
            </Typography>
          </Box>

          {/* Multiple Images */}
          <Box>
            <Typography variant="subtitle1" sx={{
              fontWeight: '600',
              color: '#1f2937',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: { xs: '14px', sm: '16px' }
            }}>
              <AutoAwesome sx={{ fontSize: { xs: '16px', sm: '20px' }, color: '#06b6d4' }} />
              Multiple Images Processing
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '12px', sm: '14px' }, mb: 1 }}>
              You can upload up to <strong>20 images at once</strong>. Each image will be processed individually.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
              <strong>{FlutterServicesAPI.getCreditLabel()} cost:</strong> Total {FlutterServicesAPI.getCreditLabel().toLowerCase()} = ({FlutterServicesAPI.getCreditLabel()} per image × Number of images)
            </Typography>
          </Box>

          {/* Token cost information */}
          <Box>
            <Typography variant="subtitle1" sx={{
              fontWeight: '600',
              color: '#1f2937',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: { xs: '14px', sm: '16px' }
            }}>
              <Star sx={{ fontSize: { xs: '16px', sm: '20px' }, color: '#f59e0b' }} />
              Token pricing
            </Typography>
            <Box sx={{
              backgroundColor: '#f8fafc',
              p: 2,
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '12px', sm: '14px' }, mb: 1, fontWeight: '600' }}>
                Token cost is calculated from your plan&apos;s content pricing matrix.
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '12px', sm: '14px' }, mb: 1 }}>
                • Add Glammify watermark: -{creditCosts.watermark} {FlutterServicesAPI.getCreditLabel().toLowerCase()} (deduction from base cost)
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '12px', sm: '14px' }, mb: 1 }}>
                • Brand watermark: no extra charge
              </Typography>
            </Box>
          </Box>

          {/* Tips */}
          <Box>
            <Typography variant="subtitle1" sx={{
              fontWeight: '600',
              color: '#1f2937',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: { xs: '14px', sm: '16px' }
            }}>
              <Info sx={{ fontSize: { xs: '16px', sm: '20px' }, color: '#059669' }} />
              Tips for Best Results
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ fontSize: { xs: '12px', sm: '14px' }, mb: 0.5 }}>
                Use high-quality, clear product images
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ fontSize: { xs: '12px', sm: '14px' }, mb: 0.5 }}>
                Ensure good lighting and minimal background clutter
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
                Check your {FlutterServicesAPI.getCreditLabel().toLowerCase()} balance before uploading multiple images
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={() => setGuidelinesOpen(false)}
          variant="contained"
          sx={{
            backgroundColor: '#6318C6',
            color: 'white',
            textTransform: 'none',
            fontWeight: '600',
            px: 3,
            py: 1,
            '&:hover': {
              backgroundColor: '#7c3aed'
            }
          }}
        >
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Helper function to format file size properly
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i];
  };

  // Caption Popup Handlers
  const handleOpenCaptionPopup = () => {
    // Synchronize per-image caption fields with already uploaded product images.
    if (uploadedFiles && uploadedFiles.length > 0) {
      const newCaptionsList = uploadedFiles.map((file, index) => {
        const existingCaption = captionsList.find(c => c.imageIndex === index) || captionsList[index];

        // Keep previously entered captions while rebuilding rows from uploaded files.
        return {
          id: existingCaption?.id || `${Date.now()}_${index}`,
          imageIndex: index,
          text: existingCaption?.text || existingCaption?.text_1 || '',
          text_1: existingCaption?.text_1 || existingCaption?.text || '',
          text_2: existingCaption?.text_2 || '',
          preview: file instanceof File ? URL.createObjectURL(file) : (typeof file === 'string' ? file : null),
        };
      });

      setCaptionsList(newCaptionsList);
    } else if (captionsList.length === 0) {
      // Keep one empty fallback row if user opens popup before uploading product images.
      setCaptionsList([{ id: Date.now(), imageIndex: 0, text: '', text_1: '', text_2: '', preview: null }]);
    }

    setIsCaptionPopupOpen(true);
  };

  const handleCaptionTextChange = (id, field, text) => {
    if (text.length <= 30) {
      setCaptionsList(prev => prev.map(c => c.id === id
        ? {
          ...c,
          [field]: text,
          ...(field === 'text_1' ? { text } : {})
        }
        : c));
    }
  };

  const submitCaptions = () => {
    const sanitizedCaptions = captionsList.map(item => ({
      text_1: (item.text_1 ?? item.text ?? '').trim(),
      text_2: (item.text_2 ?? '').trim()
    }));

    setGenerationParams(prev => ({
      ...prev,
      caption_text_1: sanitizedCaptions[0]?.text_1 || '',
      caption_text_2: sanitizedCaptions[0]?.text_2 || '',
      captions_data: sanitizedCaptions
    }));
    setIsCaptionPopupOpen(false);
  };

  // Filter model types based on selected category using database mappings

  // Main form render
  return (
    <div
      className="row-container"
      style={{
        padding: isCreatePage ? '0px 15px 100px 15px' : '0px'
      }}
    >      {/* <MerchantWelcomeSection /> */}
      <DynamicMerchantTabs />

      {/* Subscription Suspension or Zero Credits Alert */}
      {subscriptionChecked && (isSubscriptionSuspended() || hasZeroCredits()) && !FlutterServicesAPI.isFlutterApp() && (
        <Container maxWidth="lg" sx={{ mt: 3, mb: 3 }}>
          <Paper
            elevation={3}
            sx={{
              borderRadius: 3,
              border: '1px solid #fecaca',
              backgroundColor: '#fef2f2',
              overflow: 'hidden',
              animation: 'slideInDown 0.3s ease-out',
              '@keyframes slideInDown': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(-20px)'
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)'
                }
              }
            }}
          >
            <Box sx={{ p: 3 }}>
              {/* Header Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: '#fecaca',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    flexShrink: 0,
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': {
                        boxShadow: '0 0 0 0 rgba(220, 38, 38, 0.4)'
                      },
                      '70%': {
                        boxShadow: '0 0 0 10px rgba(220, 38, 38, 0)'
                      },
                      '100%': {
                        boxShadow: '0 0 0 0 rgba(220, 38, 38, 0)'
                      }
                    }
                  }}
                >
                  <Warning sx={{ color: '#dc2626', fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#dc2626',
                      fontSize: '1.25rem',
                      lineHeight: 1.2
                    }}
                  >
                    {getSubscriptionStatusMessage().title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#7f1d1d',
                      mt: 0.5,
                      fontSize: '0.875rem'
                    }}
                  >
                    Action Required
                  </Typography>
                </Box>
              </Box>

              {/* Content Section */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#374151',
                    lineHeight: 1.6,
                    fontSize: '1rem',
                    mb: 2
                  }}
                >
                  {getSubscriptionStatusMessage().message}
                </Typography>

                <Box
                  sx={{
                    backgroundColor: '#fef7f7',
                    border: '1px solid #fecaca',
                    borderRadius: 2,
                    p: 2,
                    mb: 2
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#7f1d1d',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: '#dc2626',
                        flexShrink: 0
                      }}
                    />
                    {hasZeroCredits() && !isSubscriptionSuspended()
                      ? `Please purchase a package to continue using AI generation features.`
                      : `Please renew your subscription or contact support to continue using AI generation features.`
                    }
                  </Typography>
                </Box>
              </Box>

              {/* Action Section */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {showWhatsAppSmsAuth && (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                      // Navigate to credits page with first tab (Request Credits)
                      navigate('/credits?tab=0');
                    }}
                    sx={{
                      backgroundColor: '#6318C6',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem',
                      px: 6,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      boxShadow: '0 4px 14px 0 rgba(99, 24, 198, 0.25)',
                      '&:hover': {
                        backgroundColor: '#5a16b3',
                        boxShadow: '0 6px 20px 0 rgba(99, 24, 198, 0.35)',
                        transform: 'translateY(-1px)'
                      },
                      '&:active': {
                        transform: 'translateY(0)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    {hasZeroCredits() && !isSubscriptionSuspended() ? 'Buy Package' : 'Renew Package'}
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        </Container>
      )}

      {/* Loading overlay for form data */}
      {isLoadingFormData && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} sx={{ color: '#6318C6', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
            Loading AI Generation Form...
          </Typography>
          {/* <Typography variant="body2" sx={{ color: '#6b7280', mt: 1 }}>
            Setting up your generation parameters
          </Typography> */}
        </Box>
      )}

      <Box sx={{ mt: { xs: 1, sm: 4 }, mb: 4, px: '0 !important', py: '0 !important', width: '100%', maxWidth: 'none', backgroundColor: { xs: 'transparent', sm: 'inherit' } }}>
        <Grid container spacing={{ xs: 1, sm: 4 }}>
          {/* Left Side - Upload Product */}
          <Grid item xs={12} md={6} sx={{ pt: { xs: 0, md: '20px !important' } }}>
            <Box sx={{
              position: { md: 'sticky' },
              top: { md: '78px' },
              zIndex: { md: 10 },
              height: { md: 'fit-content' }
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, pt: { xs: 1, md: 0 } }}>
                <button
                  className={`photo-tab ${activePhotoTab === 'product' ? 'active' : ''}`}
                  onClick={() => setActivePhotoTab('product')}
                >
                  Product Photo
                </button>
                <div style={{ width: '2%' }}></div>
                {canUseCustomerPhoto && (
                  <button
                    className={`photo-tab ${activePhotoTab === 'customer' ? 'active' : ''}`}
                    onClick={() => setActivePhotoTab('customer')}
                  >
                    Customer Photo
                  </button>
                )}
              </Box>
              <Paper elevation={0} sx={{
                p: { xs: 0, sm: 3 },
                borderRadius: { xs: 0, sm: 3 },
                border: { xs: 'none', sm: '2px dashed #d1d5db' },
                backgroundColor: { xs: 'transparent', sm: '#fff' },
                boxShadow: { xs: 'none', sm: '0px 4.32px 12.95px 0px rgba(0, 0, 0, 0.08)' },
                mx: { xs: 0, sm: 0 },
                mt: { xs: 0, sm: 2 },
                mb: activePhotoTab === 'customer' ? 2 : 0
              }}>

                <UploadArea
                  className="up-area"
                  dragActive={dragActive}
                  hasImage={allSelectedFiles.length > 0}
                  hasError={!!uploadError}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={(event) => {
                    // Only trigger if clicking the upload area itself, not buttons
                    if (event.target === event.currentTarget && !allSelectedFiles.length && !uploadError) {
                      handleUploadButtonClick(event);
                    }
                  }}
                >
                  {allSelectedFiles.length > 0 ? (
                    <>
                      <ImagePreview>
                        {allSelectedFiles.map((file, index) => {
                          const fileStatus = fileValidationStatus[file.name];
                          const isValid = fileStatus?.valid === true;
                          const isInvalid = fileStatus?.valid === false;
                          const error = fileStatus?.error;
                          const isSafetyBlock = fileStatus?.blockType === 'safety';
                          const preview = uploadPreviews[index];
                          const isLoadingPreview = isValid && !preview;
                          const isSingleImage = allSelectedFiles.length === 1;

                          return (
                            <Box
                              key={index}
                              className={`image-item ${isSingleImage ? 'single-image' : ''} ${isInvalid ? 'invalid-image' : ''} ${isLoadingPreview ? 'loading-image' : ''}`}
                            >
                              {isValid && preview ? (
                                <>
                                  <img src={preview} alt={`Product preview ${index + 1}`} />
                                  <Box className="change-image-overlay" onClick={() => changeImage(index)}>
                                    <Button variant="contained" size="small" startIcon={<CloudUpload />} sx={{ backgroundColor: 'rgba(147, 51, 238, 0.9)', color: 'white' }}>
                                      Change Image
                                    </Button>
                                  </Box>
                                </>
                              ) : isLoadingPreview ? (
                                <Box sx={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#f0f9ff',
                                  borderRadius: '12px',
                                }}>
                                  <CircularProgress size={32} sx={{ color: '#6318C6' }} />
                                </Box>
                              ) : isInvalid ? (
                                <UploadPreviewErrorBox isSafetyBlock={isSafetyBlock} error={error} />
                              ) : null}
                              <RemoveButton onClick={(e) => { e.stopPropagation(); removeImage(index); }}>
                                <Close />
                              </RemoveButton>
                            </Box>
                          );
                        })}
                      </ImagePreview>

                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <button onClick={(event) => handleUploadButtonClick(event)} className="btn-click border-outln">
                          Add More Images
                        </button>
                        {!FlutterServicesAPI.isFlutterApp() && (
                          <Box sx={{ mt: 0, textAlign: 'center' }}>
                            <span
                              className="guideline-link"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`${process.env.PUBLIC_URL}/assets/file/Glammify user manual.pdf`, '_blank', 'noopener,noreferrer');
                              }}
                            >
                              View upload guidelines <ArrowForward sx={{ fontSize: 18 }} />
                            </span>
                          </Box>
                        )}
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box sx={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        <img src={`${process.env.PUBLIC_URL}/assets/img/icons/upload_photo.svg`} alt="Upload Product" style={{ width: '28px', height: '28px' }} />
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontSize: { xs: '16.26px', sm: '18px', md: '18px' },
                          fontWeight: '600',
                          fontFamily: '"Montserrat", sans-serif',
                          color: '#1E2939',
                          mb: 1
                        }}
                      >
                        Upload Product Photo
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 2,
                          fontSize: { xs: '11.88px', sm: '14px' },
                          color: '#6A7282',
                          fontFamily: '"Montserrat", sans-serif',
                          fontWeight: 400,
                          maxWidth: '400px',
                          lineHeight: 1.4,
                          textAlign: 'center'
                        }}
                      >
                        Drag & drop or click to select multiple images.<br />
                        PNG, JPG, JPEG, WebP, HEIC up to 12MB each.<br />
                        Max 20 images
                      </Typography>
                      <button className="btn-click" onClick={(event) => handleUploadButtonClick(event)}>
                        Choose Files
                      </button>
                      {!FlutterServicesAPI.isFlutterApp() && (
                        <span
                          className="guideline-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`${process.env.PUBLIC_URL}/assets/file/Glammify user manual.pdf`, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          View upload guidelines <ArrowForward sx={{ fontSize: 18 }} />
                        </span>
                      )}
                    </>
                  )}
                </UploadArea>

                <VisuallyHiddenInput
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.heic"
                  onChange={handleFileUpload}
                  multiple
                />

                {activePhotoTab === 'product' && uploadError && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {uploadError}
                  </Alert>
                )}
              </Paper>

              {/* Customer Photo Section - Only shown when in Customer tab */}
              {activePhotoTab === 'customer' && (
                <Paper elevation={0} sx={{
                  p: { xs: 0, sm: 3 },
                  borderRadius: { xs: 0, sm: 3 },
                  border: { xs: 'none', sm: '2px dashed #d1d5db' },
                  backgroundColor: { xs: 'transparent', sm: '#fff' },
                  boxShadow: { xs: 'none', sm: '0px 4.32px 12.95px 0px rgba(0, 0, 0, 0.08)' },
                  mx: { xs: 0, sm: 0 },
                  mt: 0
                }}>
                  <UploadArea
                    className="up-area"
                    dragActive={false}
                    hasImage={!!customerFile}
                    hasError={!!customerUploadError || customerSafetyBlocked}
                    onClick={(event) => {
                      if (event.target === event.currentTarget && !customerFile && !customerUploadError && !customerSafetyBlocked) {
                        handleCustomerUploadButtonClick(event);
                      }
                    }}
                  >
                    {customerFile ? (
                      <>
                        <ImagePreview>
                          <Box
                            className={`image-item single-image${customerSafetyBlocked || customerUploadError ? ' invalid-image' : ''}`}
                            sx={{ position: 'relative' }}
                          >
                            {customerPreview && !customerSafetyBlocked && !customerUploadError ? (
                              <>
                                <img src={customerPreview} alt="Customer preview" />
                                <Box className="change-image-overlay" onClick={() => handleCustomerUploadButtonClick()}>
                                  <Button variant="contained" size="small" startIcon={<CloudUpload />} sx={{ backgroundColor: 'rgba(147, 51, 238, 0.9)', color: 'white' }}>
                                    Change Image
                                  </Button>
                                </Box>
                              </>
                            ) : (
                              <UploadPreviewErrorBox
                                isSafetyBlock={customerSafetyBlocked}
                                error={customerUploadError}
                              />
                            )}
                            <RemoveButton onClick={(e) => { e.stopPropagation(); removeCustomerFile(); }}>
                              <Close />
                            </RemoveButton>
                          </Box>
                        </ImagePreview>
                      </>
                    ) : customerUploadError ? (
                      <>
                        <UploadPreviewErrorBox isSafetyBlock={false} error={customerUploadError} />
                        <button
                          className="btn-click"
                          onClick={(event) => {
                            setCustomerUploadError('');
                            handleCustomerUploadButtonClick(event);
                          }}
                          style={{ marginTop: 16 }}
                        >
                          Try Again
                        </button>
                      </>
                    ) : (
                      <>
                        <Box sx={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                          <img src={`${process.env.PUBLIC_URL}/assets/img/icons/customer_photo.svg`} alt="Upload Customer" style={{ width: '28px', height: '28px' }} />
                        </Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontSize: { xs: '16.26px', sm: '18px', md: '18px' },
                            fontWeight: '600',
                            fontFamily: '"Montserrat", sans-serif',
                            color: '#1E2939',
                            mb: 1
                          }}
                        >
                          Upload Customer Photo
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 2,
                            fontSize: { xs: '11.88px', sm: '14px' },
                            color: '#6A7282',
                            fontFamily: '"Montserrat", sans-serif',
                            fontWeight: 400,
                            maxWidth: '400px',
                            lineHeight: 1.4,
                            textAlign: 'center'
                          }}
                        >
                          Upload your customer's photo.<br />Only 1 image allowed.
                        </Typography>
                        <button className="btn-click" onClick={(event) => handleCustomerUploadButtonClick(event)}>
                          Choose File
                        </button>
                      </>
                    )}
                  </UploadArea>
                  <input
                    ref={customerFileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,.heic"
                    onChange={handleCustomerFileUpload}
                    style={{ display: 'none' }}
                  />
                </Paper>
              )}
            </Box>
          </Grid>

          {/* Right Side - AI Attributes */}
          <Grid item xs={12} md={6} sx={{ pt: { xs: 0, md: '20px !important' } }}>
            <Paper elevation={0} sx={{
              p: { xs: 0, sm: 3 },
              borderRadius: 3,
              border: { xs: 'none', sm: '2px solid #6318C6' },
              backgroundColor: { xs: 'transparent', sm: '#fff' },
              transition: 'border 0.3s ease',
              boxShadow: 'none',
            }}>
              <SectionTitle className="ai-attributes-title" sx={{
                marginBottom: { xs: 2, sm: 3, md: 2 },
                marginTop: { xs: 2.5, sm: 0, md: 0 }
              }}>
                <AutoAwesome sx={{ mr: 1, verticalAlign: 'middle' }} />
                AI Attributes
              </SectionTitle>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* 1. Category type */}
                <Box>
                  <FormLabel component="legend" className="ab-form-label">
                    Choose Jewellery Type
                  </FormLabel>
                  <Box className="select-grid">
                    {options?.categories
                      ?.filter(
                        (category) =>
                          activePhotoTab !== 'customer' ||
                          !isCategoryHiddenForCustomerPhotoTab(category)
                      )
                      ?.sort((a, b) =>
                        (a.display_name || a.name).localeCompare(
                          b.display_name || b.name
                        )
                      )
                      .map((category) => {
                        const categoryId = category.id?.toString();
                        const isSelected = generationParams.category === categoryId;

                        return (
                          <Box
                            key={category.id}
                            className={`select-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              const isSame = generationParams.category === categoryId;

                              setGenerationParams((prev) => ({
                                ...prev,

                                // ✅ Toggle category
                                category: isSame ? '' : categoryId,

                                // ✅ Reset ONLY when selecting new category
                                model_type: isSame ? prev.model_type : '',
                                bust_color: isSame ? prev.bust_color : '',
                                ethnicity: isSame ? prev.ethnicity : '',
                                clothing_type: isSame ? prev.clothing_type : '',
                                bust_finish: isSame ? prev.bust_finish : '',
                                gender_option_id: isSame
                                  ? prev.gender_option_id
                                  : getFemaleOptionId(),
                                selected_ai_model: isSame ? prev.selected_ai_model : ''
                              }));

                              setFieldErrors((prev) => ({ ...prev, category: '' }));

                              // ❗ Call API only when selecting (not unselecting)
                              if (!isSame) {
                                loadOptions();
                              }
                            }}
                          >
                            <Box className={`select-box ${isSelected ? 'selected' : ''}`}>
                              {isSelected && (
                                <Box className="select-checkbox">
                                  <Check sx={{ fontSize: '14px', color: '#651BC6' }} />
                                </Box>
                              )}

                              {category.icon_url ? (
                                <img
                                  src={getImageUrl(category.icon_url)}
                                  alt={category.display_name || category.name}
                                  className="select-image"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <ImageIcon sx={{ fontSize: 40, color: '#D1D1D1' }} />
                              )}
                            </Box>

                            <Typography className="select-label">
                              {category.display_name || category.name}
                            </Typography>
                          </Box>
                        );
                      })}
                  </Box>
                  {fieldErrors.category && (
                    <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: '14px', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500 }}>
                      {fieldErrors.category}
                    </Typography>
                  )}
                </Box>

                {/* 2. Output format */}
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" className="ab-form-label">
                    Output Format
                  </FormLabel>
                  <RadioGroup
                    value={generationParams.output_format}
                    onChange={handleOutputFormatChange}
                  >
                    {options?.output_formats && (() => {
                      // If Product Only is selected, hide VIDEO format option
                      const availableFormats = options.output_formats.filter(of => {
                        if (of.name === 'VIDEO' && isProductOnlyModel()) {
                          return false;
                        }
                        return true;
                      });

                      return Array.from({ length: Math.ceil(availableFormats.length / 2) }, (_, rowIndex) => (
                        <Box key={rowIndex} sx={{
                          display: 'flex',
                          width: '100%',
                          gap: 1,
                          mb: 1,
                          flexDirection: 'row'
                        }}>
                          {availableFormats.slice(rowIndex * 2, rowIndex * 2 + 2).map((format) => (
                            <FormControlLabel
                              key={format.id}
                              value={format.id}
                              control={<Radio />}
                              label={
                                <>
                                  {getOutputFormatIcon(format)}
                                  <span>{format.display_name}</span>
                                </>
                              }
                              className={`select-radio ${generationParams.output_format === format.id.toString() ? 'selected' : ''}`}
                            />
                          ))}
                        </Box>
                      ));
                    })()}

                    {fieldErrors.output_format && (
                      <Typography sx={{
                        color: '#dc2626',
                        fontSize: '12px',
                        mt: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontFamily: 'var(--font-montserrat-medium)',
                        fontWeight: 500
                      }}>
                        ⚠️ {fieldErrors.output_format}
                      </Typography>
                    )}
                  </RadioGroup>
                </FormControl>

                {/* 3. Model types — hidden on Customer Photo tab; API still receives Human Model via effect + state. */}
                {activePhotoTab !== 'customer' && (
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" className="ab-form-label">
                      Model Type
                    </FormLabel>
                    <RadioGroup
                      value={generationParams.model_type}
                      onChange={handleModelTypeChange}
                    >
                      {/* Render model types in rows of 2 */}
                      {filteredModelTypes &&
                        Array.from({ length: Math.ceil(filteredModelTypes.length / 2) }, (_, rowIndex) => (
                          <Box key={rowIndex} sx={{
                            display: 'flex',
                            width: '100%',
                            gap: 1,
                            mb: 1,
                            flexDirection: { xs: 'column', sm: 'column', md: 'row' }
                          }}>
                            {filteredModelTypes.sort((a, b) => (a.display_name || a.name).localeCompare(b.display_name || b.name)).slice(rowIndex * 2, rowIndex * 2 + 2).map((type) => (
                              <FormControlLabel
                                key={type.id}
                                value={type.id}
                                control={<Radio />}
                                label={
                                  <>
                                    {getModelTypeIcon(type)}
                                    <span>{type.display_name}</span>
                                  </>
                                }
                                className={`select-radio ${generationParams.model_type === type.id.toString() ? 'selected' : ''}`}
                              />
                            ))}
                          </Box>
                        ))
                      }
                    </RadioGroup>
                    {fieldErrors.model_type && (
                      <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: '14px', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500 }}>
                        {fieldErrors.model_type}
                      </Typography>
                    )}
                  </FormControl>
                )}

                {/* 4. Gender */}
                {getFieldVisibility('gender') && (
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" className="ab-form-label">
                      Gender
                    </FormLabel>
                    <RadioGroup
                      value={generationParams.gender_option_id}
                      onChange={(e) => setGenerationParams(prev => ({ ...prev, gender_option_id: e.target.value }))}
                    >
                      {options?.gender_options && (
                        (() => {
                          const filteredGenders = options.gender_options.filter(g =>
                            options?.category_gender_visibility?.[String(generationParams.category)]?.includes(g.id)
                          );
                          return Array.from({ length: Math.ceil(filteredGenders.length / 2) }, (_, rowIndex) => (
                            <Box key={rowIndex} sx={{
                              display: 'flex',
                              width: '100%',
                              gap: 1,
                              mb: 1,
                              flexDirection: 'row'
                            }}>
                              {filteredGenders.slice(rowIndex * 2, rowIndex * 2 + 2).map((gender) => (
                                <FormControlLabel
                                  key={gender.id}
                                  value={gender.id.toString()}
                                  control={<Radio />}
                                  label={
                                    <>
                                      {gender.preview_image_url ? (
                                        <img
                                          src={getImageUrl(gender.preview_image_url)}
                                          alt={gender.display_name}
                                          style={{ width: '20px', height: '20px', marginRight: '0px', objectFit: 'contain', borderRadius: '4px' }}
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      ) : (
                                        <Person sx={{ mr: 1 }} />
                                      )}
                                      <span>{gender.display_name}</span>
                                    </>
                                  }
                                  className={`select-radio ${generationParams.gender_option_id === gender.id.toString() ? 'selected' : ''}`}
                                />
                              ))}
                            </Box>
                          ));
                        })()
                      )}
                    </RadioGroup>
                    {fieldErrors.gender_option_id && (
                      <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: '14px', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500 }}>
                        {fieldErrors.gender_option_id}
                      </Typography>
                    )}
                  </FormControl>
                )}

                {/* 4b. Theme (only visible when Product Only model type is selected) */}
                {isProductOnlyModel() && options?.themes?.length > 0 && (
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" className="ab-form-label">
                      Theme
                    </FormLabel>
                    <RadioGroup
                      value={generationParams.theme}
                      onChange={(e) => setGenerationParams(prev => ({ ...prev, theme: e.target.value }))}
                    >
                      {Array.from({ length: Math.ceil(options.themes.length / 2) }, (_, rowIndex) => (
                        <Box key={rowIndex} sx={{
                          display: 'flex',
                          width: '100%',
                          gap: 1,
                          mb: 1,
                          flexDirection: 'row'
                        }}>
                          {options.themes.slice(rowIndex * 2, rowIndex * 2 + 2).map((theme) => (
                            <FormControlLabel
                              key={theme.id}
                              value={theme.id.toString()}
                              control={<Radio />}
                              label={
                                <>
                                  <Palette sx={{ mr: 0.5, fontSize: '18px' }} />
                                  <span>{theme.display_name}</span>
                                </>
                              }
                              className={`select-radio ${generationParams.theme === theme.id.toString() ? 'selected' : ''}`}
                            />
                          ))}
                        </Box>
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}

                {/* 5. Media quality */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FormLabel component="legend" className="ab-form-label" sx={{ mb: '0 !important', width: 'auto', mr: 1 }}>
                      Media Quality
                    </FormLabel>
                    <IconButton
                      onClick={handleInfoClick}
                      size="small"
                      sx={{ p: 0, color: '#1F2937' }}
                    >
                      <Info sx={{ fontSize: '18px' }} />
                    </IconButton>
                  </Box>

                  <Popover
                    open={isInfoOpen}
                    anchorEl={infoAnchorEl}
                    onClose={handleInfoClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    PaperProps={{
                      sx: {
                        borderRadius: '24px',
                        mt: 1.5,
                        boxShadow: '0px 10px 40px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #E5E7EB',
                        width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
                        maxWidth: { xs: '100%', sm: '600px' },
                        overflowX: 'auto',
                        overflowY: 'hidden'
                      }
                    }}
                  >
                    <Box sx={{ p: { xs: 2, sm: 4 }, bgcolor: 'white' }}>
                      <Typography className="media-title">
                        Media Quality
                      </Typography>
                      {filteredMediaQualities.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                          {!generationParams.output_format
                            ? 'Select an output format to preview quality options.'
                            : 'No options configured for this output type.'}
                        </Typography>
                      ) : null}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: { xs: 3, sm: 6 },
                      }}>
                        {filteredMediaQualities.map((item, idx) => {
                          const size = 60 + (idx * 20);
                          return (
                            <Box key={item.id || item.name || idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Box
                                component="img"
                                src={item.preview_image_url ? getImageUrl(item.preview_image_url) : `${process.env.PUBLIC_URL}/assets/img/icons/media_sample.svg`}
                                alt={item.display_name}
                                sx={{
                                  width: size,
                                  height: size,
                                  objectFit: 'cover',
                                  borderRadius: '11px',
                                  mb: '10px',
                                  boxShadow: idx === 2 ? '0px 4px 15px rgba(0, 0, 0, 0.05)' : 'none'
                                }}
                              />
                              <Typography className="media-label">
                                {item.display_name}
                              </Typography>
                              <Typography className="media-res">
                                {item.description}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Popover>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {filteredMediaQualities.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        {!generationParams.output_format
                          ? 'No IMAGE-mapped media quality options found.'
                          : 'No media quality options for this output type.'}
                      </Typography>
                    ) : (
                      filteredMediaQualities.map((quality) => (
                        <QualityButton
                          key={quality.id || quality.name}
                          selected={generationParams.media_quality_id === quality.id}
                          onClick={() => handleMediaQualityChange(quality.id)}
                        >
                          {quality.display_name || quality.name}
                        </QualityButton>
                      ))
                    )}
                  </Box>
                </Box>

                {/* 6. Custom tags */}
                <Box sx={{ mb: 2 }}>
                  <FormLabel component="legend" className="ab-form-label">
                    Custom Tags (Optional)
                  </FormLabel>
                  <Box sx={{ position: 'relative' }}>
                    <TagsContainer>
                      {generationParams.custom_tags.map((tag, index) => (
                        <StyledTagChip
                          key={index}
                          label={tag}
                          onClick={() => handleEditTag(tag)}
                          onDelete={() => handleRemoveTag(tag)}
                          deleteIcon={<Close />}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                      <TagInput
                        placeholder={generationParams.custom_tags.length === 0 ? "Search or Add Tags" : ""}
                        value={tagInput}
                        onChange={handleTagInputChange}
                        onFocus={() => setIsTagInputFocused(true)}
                        onBlur={() => setIsTagInputFocused(false)}
                        onKeyDown={handleTagInputKeyDown}
                      />
                    </TagsContainer>

                    {isTagInputFocused && tagInput.trim() && customTagSuggestions.length > 0 && (
                      <Paper
                        elevation={3}
                        sx={{
                          position: 'absolute',
                          top: 'calc(100% + 6px)',
                          left: 0,
                          right: 0,
                          zIndex: 20,
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          maxHeight: '180px',
                          overflowY: 'auto'
                        }}
                      >
                        {customTagSuggestions.map((suggestion) => (
                          <Box
                            key={suggestion}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleTagSuggestionSelect(suggestion)}
                            sx={{
                              px: 1.5,
                              py: 1,
                              fontSize: '14px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #F3F4F6',
                              '&:last-of-type': { borderBottom: 'none' },
                              '&:hover': { backgroundColor: '#F9FAFB' }
                            }}
                          >
                            {suggestion}
                          </Box>
                        ))}
                      </Paper>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '13px', marginTop: '5px', fontWeight: 400, color: '#6B7280' }}>
                    *Note: Add comma after each tag.
                  </Typography>
                </Box>

                {/* 7. Bust finish */}
                {getFieldVisibility('bust_finish') && (
                  <Box>
                    <FormLabel component="legend" className="ab-form-label">
                      Bust Finish
                    </FormLabel>
                    <Box className="select-grid">
                      {getFilteredOptions('bust_finishes')
                        ?.sort((a, b) => a.display_name.localeCompare(b.display_name))
                        .map((bustFinish) => {
                          const finishId = bustFinish.id?.toString();
                          const isSelected = generationParams.bust_finish === finishId;

                          return (
                            <Box
                              key={bustFinish.id}
                              className={`select-item ${isSelected ? 'selected' : ''}`}
                              onClick={async () => {
                                const isSame = generationParams.bust_finish === finishId;

                                const nextFinish = isSame ? '' : finishId;

                                setGenerationParams(prev => ({
                                  ...prev,
                                  bust_finish: nextFinish,
                                  bust_color: isSame ? prev.bust_color : ''
                                }));

                                setFieldErrors(prev => ({ ...prev, bust_finish: '' }));
                                if (isSame) {
                                  if (originalOptions?.bust_colors) {
                                    setOptions(prev => ({
                                      ...prev,
                                      bust_colors: originalOptions.bust_colors
                                    }));
                                  }
                                  return;
                                }

                                // ✅ If selecting → fetch filtered colors
                                try {
                                  const response = await aiGenerationAPI.getBustColorsByFinish(finishId);

                                  if (
                                    response.data &&
                                    response.data.bust_colors &&
                                    response.data.bust_colors.length > 0
                                  ) {
                                    const planScoped = filterBustColorsByPlanAllowlist(response.data.bust_colors);
                                    setOptions(prev => ({
                                      ...prev,
                                      bust_colors: planScoped
                                    }));
                                  } else if (originalOptions?.bust_colors) {
                                    setOptions(prev => ({
                                      ...prev,
                                      bust_colors: originalOptions.bust_colors
                                    }));
                                  }
                                } catch (error) {
                                  console.error('❌ Failed to fetch bust colors for finish:', error);

                                  if (originalOptions?.bust_colors) {
                                    setOptions(prev => ({
                                      ...prev,
                                      bust_colors: originalOptions.bust_colors
                                    }));
                                  }
                                }
                              }}
                            >
                              <Box
                                className={`select-box ${isSelected ? 'selected' : ''
                                  } attribute-select-box`}
                              >
                                {isSelected && (
                                  <Box className="select-checkbox">
                                    <Check sx={{ fontSize: '14px', color: '#651BC6' }} />
                                  </Box>
                                )}

                                {bustFinish.preview_image_url ? (
                                  <img
                                    src={getImageUrl(bustFinish.preview_image_url)}
                                    alt={bustFinish.display_name || bustFinish.name}
                                    className="select-image attribute-select-image"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <Accessibility sx={{ fontSize: 40, color: '#D1D1D1' }} />
                                )}
                              </Box>

                              <Typography className="select-label">
                                {bustFinish.display_name || bustFinish.name}
                              </Typography>
                            </Box>
                          );
                        })}
                    </Box>
                    {fieldErrors.bust_finish && (
                      <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: '14px', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500 }}>
                        {fieldErrors.bust_finish}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* 8. Bust color */}
                {getFieldVisibility('bust_color') && (() => {
                  const bustColorOptions = getFilteredOptions('bust_colors');
                  return (
                  <Box>
                    <FormLabel component="legend" className="ab-form-label">
                      Bust Color
                    </FormLabel>

                    {/* Keep bust color below bust finish for the intended selection flow. */}
                    <Box
                      onClick={() => setBustColorOpen(prev => !prev)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: '14px 16px',
                        borderRadius: '10px',
                        border: bustColorOpen ? '2px solid #6318C6' : '1.5px solid #d1d5db',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s ease',
                        height: '45px',
                        '&:hover': { borderColor: '#9ca3af' },
                      }}
                    >
                      {generationParams.bust_color ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: '20px',
                              height: '20px',
                              backgroundColor: bustColorOptions.find(
                                c => c.id === parseInt(generationParams.bust_color)
                              )?.hex_color || '#cccccc',
                              borderRadius: '4px',
                              border: '1px solid rgba(0,0,0,0.1)',
                              flexShrink: 0,
                            }}
                          />
                          <Typography sx={{ fontWeight: '500', fontSize: '11.08px', color: '#1E2939', fontFamily: 'var(--font-montserrat-medium)' }}>
                            {bustColorOptions.find(c => c.id === parseInt(generationParams.bust_color))?.display_name}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ color: '#9ca3af', fontWeight: '500', fontSize: '15.15px', fontFamily: 'var(--font-montserrat-medium)' }}>
                          Select Bust Color
                        </Typography>
                      )}
                      <KeyboardArrowDown
                        sx={{
                          color: '#6B7280',
                          fontSize: '22px',
                          transform: bustColorOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                        }}
                      />
                    </Box>

                    {/* Expandable color palette grid */}
                    {bustColorOpen && (
                      <Box
                        sx={{
                          mt: 0,
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: '6px',
                          backgroundColor: '#FFFFFF',
                          p: 1,
                          borderRadius: '10px',
                          border: '1px solid #DEDEDE'
                        }}
                      >
                        {bustColorOptions.length === 0 ? (
                          <Typography sx={{ color: '#9ca3af', fontSize: '14px', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, gridColumn: 'span 3' }}>
                            No bust colors available
                          </Typography>
                        ) : (
                          bustColorOptions
                            .sort((a, b) => a.display_name.localeCompare(b.display_name))
                            .map((color) => {
                              const isSelected =
                                generationParams.bust_color === color.id.toString() ||
                                generationParams.bust_color === color.id;
                              return (
                                <Box
                                  key={color.id}
                                  onClick={() => {
                                    setGenerationParams(prev => ({ ...prev, bust_color: color.id.toString() }));
                                    setBustColorOpen(false);
                                  }}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    p: '5px 5px',
                                    borderRadius: '5.42px',
                                    border: isSelected ? '2px solid #6318C6' : '1.5px solid #DEDEDE',
                                    backgroundColor: isSelected ? '#F3F0FF' : '#EFEFEF',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      borderColor: '#6318C6',
                                      backgroundColor: isSelected ? '#F3F0FF' : '#ECEDEF',
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: '28px',
                                      height: '28px',
                                      minWidth: '28px',
                                      backgroundColor: color.hex_color || '#cccccc',
                                      borderRadius: '5px',
                                      border: '1px solid rgba(0,0,0,0.1)',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontWeight: '500',
                                      fontSize: '11.5px',
                                      color: isSelected ? '#6318C6' : '#1F2937',
                                      fontFamily: 'var(--font-montserrat-medium)',
                                      lineHeight: 1.3,
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {color.display_name}
                                  </Typography>
                                </Box>
                              );
                            })
                        )}
                      </Box>
                    )}

                    {fieldErrors.bust_color && (
                      <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: '14px', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500 }}>
                        {fieldErrors.bust_color}
                      </Typography>
                    )}
                  </Box>
                  );
                })()}

                {/* 9. Select Your AI Model — hidden when ethnicity is chosen (mutually exclusive with section 10), Human Hand, Bust, or Product Only */}
                {canUseModelFaceUi && activePhotoTab !== 'customer' && !generationParams.ethnicity && !isHumanHandSelected && !isBustSelected && !isProductOnlyModel() && (

                  <Box>
                    <FormLabel component="legend" className="ab-form-label">
                      Select Your AI Model
                    </FormLabel>
                    <Box className="select-grid">
                      {assignedAiModels.map((model) => {
                        const modelId = model?.id?.toString?.() || '';
                        const isSelected = generationParams.selected_ai_model === modelId;
                        const previewUrl = getThumbnailUrl(model);
                        return (
                          <Box
                            key={modelId}
                            className={`select-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              const nextId = isSelected ? '' : modelId;
                              setGenerationParams(prev => ({
                                ...prev,
                                selected_ai_model: nextId,
                                // Clear manual ethnicity when picking a catalog model (ethnicity_id on generate comes from the model row).
                                ethnicity: nextId ? '' : prev.ethnicity
                              }));
                              if (nextId) {
                                setFieldErrors(prev => ({ ...prev, ethnicity: '' }));
                              }
                            }}
                          >
                            <Box className={`select-box ${isSelected ? 'selected' : ''} attribute-select-box model-face-select-box`}>
                              {isSelected && (
                                <Box className="select-checkbox">
                                  <Check sx={{ fontSize: '14px', color: '#651BC6' }} />
                                </Box>
                              )}
                              <SecureImage
                                src={previewUrl}
                                alt={model.display_name || model.name || 'AI Model'}
                                useThumbnail={true}
                                className="select-image model-face-select-image"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </Box>
                            <Typography className="select-label">
                              {model.display_name || model.name}
                            </Typography>
                          </Box>
                        );
                      })}
                      {assignedAiModels.length === 0 && (
                        <Box sx={{ gridColumn: '1 / -1' }}>
                          <Typography variant="body2" sx={{ color: '#6b7280', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500 }}>
                            You don't have any AI models yet. Purchase a model from AI Model Studio.
                          </Typography>
                          {canViewModelStudio && <Button
                            className="ab-button"
                            variant="outlined"
                            size="small"
                            disabled={!showWhatsAppSmsAuth && FlutterServicesAPI.isFlutterApp()}
                            onClick={() => {
                              if (!showWhatsAppSmsAuth && FlutterServicesAPI.isFlutterApp()) return; // Block navigation during app review
                              const modelStudioDraft = {
                                generationParams,
                                activePhotoTab,
                                captionsList,
                                selectedBrandLogoId,
                                uploadedFiles,
                                uploadPreviews,
                                allSelectedFiles,
                                fileValidationStatus,
                                customerFile,
                                customerPreview,
                                customerUploadError
                              };
                              setModelStudioDraft(modelStudioDraft);
                              // Save form state so it can be restored when user returns from AI Model Studio.
                              // We only store lightweight primitives (IDs, strings) — no file/binary data.
                              const fullSnapshot = {
                                generationParams,
                                activePhotoTab,
                                captionsList,
                                selectedBrandLogoId
                              };
                              // Minimal fallback: only scalar fields from generationParams (safest subset)
                              const minimalSnapshot = {
                                generationParams: {
                                  category: generationParams.category,
                                  model_type: generationParams.model_type,
                                  output_format: generationParams.output_format,
                                  bust_color: generationParams.bust_color,
                                  ethnicity: generationParams.ethnicity,
                                  clothing_type: generationParams.clothing_type,
                                  bust_finish: generationParams.bust_finish,
                                  gender_option_id: generationParams.gender_option_id,
                                  watermark: generationParams.watermark,
                                  brand_watermark: generationParams.brand_watermark,
                                  media_quality_id: generationParams.media_quality_id,
                                  selected_ai_model: generationParams.selected_ai_model
                                },
                                activePhotoTab,
                                selectedBrandLogoId
                              };
                              try {
                                sessionStorage.setItem('ai_generation_restore_state', JSON.stringify(fullSnapshot));
                              } catch (e) {
                                // QuotaExceededError or SecurityError — fall back to minimal data
                                console.warn('sessionStorage quota exceeded, saving minimal state:', e);
                                try {
                                  sessionStorage.setItem('ai_generation_restore_state', JSON.stringify(minimalSnapshot));
                                } catch (e2) {
                                  console.error('sessionStorage unavailable, state will not be restored:', e2);
                                }
                              }
                              try { localStorage.setItem(AI_MODEL_STUDIO_RETURN_KEY, '1'); } catch (_) { }
                              navigate('/profile/ai-model-studio', {
                                state: {
                                  returnToGenerate: true,
                                  aiGenerationDraft: modelStudioDraft
                                }
                              });
                            }}
                            sx={{
                              mt: 1.5,
                              textTransform: 'none',
                              borderColor: showWhatsAppSmsAuth ? '#6318C6' : '#E5E7EB',
                              color: showWhatsAppSmsAuth ? '#6318C6' : '#9CA3AF',
                              '&:hover': {
                                borderColor: showWhatsAppSmsAuth ? '#6318C6' : '#E5E7EB',
                                backgroundColor: showWhatsAppSmsAuth ? 'rgba(99, 24, 198, 0.04)' : 'transparent',
                              },
                              '&.Mui-disabled': {
                                borderColor: '#E5E7EB',
                                color: '#9CA3AF'
                              }
                            }}
                          >
                            {showWhatsAppSmsAuth ? 'Go to AI Model Studio' : 'Coming Soon'}
                          </Button>}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* 10. Ethnicity — hidden on Customer Photo tab and when AI model face is chosen */}
                {!generationParams.selected_ai_model &&
                  activePhotoTab !== 'customer' &&
                  getFieldVisibility('ethnicity') &&
                  getFilteredOptions('ethnicities').length > 0 &&
                  (!isHumanHandSelected || allowEthnicityForFemaleRing) && (
                    <Box>
                      <FormLabel component="legend" className="ab-form-label">
                        Ethnicity
                      </FormLabel>
                      <Box className="select-grid">
                        {getFilteredOptions('ethnicities')?.map((ethnicity) => {
                          const ethnicityId = ethnicity.id?.toString();
                          const isSelected = generationParams.ethnicity === ethnicityId;

                          return (
                            <Box
                              key={ethnicity.id}
                              className={`select-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                setGenerationParams(prev => {
                                  const isSame = prev.ethnicity === ethnicityId;

                                  return {
                                    ...prev,
                                    ethnicity: isSame ? '' : ethnicityId,
                                    selected_ai_model: isSame ? prev.selected_ai_model : ''
                                  };
                                });

                                setFieldErrors(prev => ({ ...prev, ethnicity: '' }));
                              }}
                            >
                              <Box
                                className={`select-box ${isSelected ? 'selected' : ''
                                  } attribute-select-box`}
                              >
                                {isSelected && (
                                  <Box className="select-checkbox">
                                    <Check sx={{ fontSize: '14px', color: '#651BC6' }} />
                                  </Box>
                                )}

                                {ethnicity.preview_image_url ? (
                                  <img
                                    src={getImageUrl(ethnicity.preview_image_url)}
                                    alt={ethnicity.display_name || ethnicity.name}
                                    className="select-image attribute-select-image"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <Person sx={{ fontSize: 40, color: '#D1D1D1' }} />
                                )}
                              </Box>

                              <Typography className="select-label">
                                {ethnicity.display_name || ethnicity.name}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                      {fieldErrors.ethnicity && (
                        <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: '14px', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500 }}>
                          {fieldErrors.ethnicity}
                        </Typography>
                      )}
                    </Box>
                  )}

                {/* 11. Clothing types */}
                {getFieldVisibility('clothing_type') && (
                  <Box>
                    <FormLabel component="legend" className="ab-form-label">
                      Clothing Type
                    </FormLabel>
                    <Box className="select-grid">
                      {getFilteredOptions('clothing_types')
                        ?.sort((a, b) => a.display_name.localeCompare(b.display_name))
                        .map((clothingType) => {
                          const clothingId = clothingType.id?.toString();
                          const isSelected = generationParams.clothing_type === clothingId;

                          return (
                            <Box
                              key={clothingType.id}
                              className={`select-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                setGenerationParams(prev => {
                                  const isSame = prev.clothing_type === clothingId;

                                  return {
                                    ...prev,
                                    clothing_type: isSame ? '' : clothingId
                                  };
                                });

                                setFieldErrors(prev => ({ ...prev, clothing_type: '' }));
                              }}
                            >
                              <Box
                                className={`select-box ${isSelected ? 'selected' : ''
                                  } attribute-select-box`}
                              >
                                {isSelected && (
                                  <Box className="select-checkbox">
                                    <Check sx={{ fontSize: '14px', color: '#651BC6' }} />
                                  </Box>
                                )}

                                {clothingType.preview_image_url ? (
                                  <img
                                    src={getImageUrl(clothingType.preview_image_url)}
                                    alt={clothingType.display_name || clothingType.name}
                                    className="select-image attribute-select-image"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <ImageIcon sx={{ fontSize: 40, color: '#D1D1D1' }} />
                                )}
                              </Box>

                              <Typography className="select-label">
                                {clothingType.display_name || clothingType.name}
                              </Typography>
                            </Box>
                          );
                        })}
                    </Box>
                    {fieldErrors.clothing_type && (
                      <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: '14px', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500 }}>
                        {fieldErrors.clothing_type}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* 12. Captions */}
                <Box sx={{ mb: 0 }}>
                  <FormLabel component="legend" variant="body2" className="ab-form-label" sx={{ mb: 1, display: 'block' }}>
                    Do you want to add a caption?
                  </FormLabel>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box
                      onClick={() => {
                        setGenerationParams(prev => ({ ...prev, add_caption: true }));
                        handleOpenCaptionPopup();
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        border: '1.5px solid #6318C6',
                        borderRadius: '12px',
                        py: '10px',
                        px: 2,
                        cursor: 'pointer',
                        backgroundColor: generationParams.add_caption ? '#F9F6FE' : 'transparent',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <Radio
                        checked={generationParams.add_caption === true}
                        onChange={() => {
                          setGenerationParams(prev => ({ ...prev, add_caption: true }));
                          handleOpenCaptionPopup();
                        }}
                        value="yes"
                        name="add-caption-group"
                        sx={{
                          color: '#6318C6',
                          '&.Mui-checked': {
                            color: '#6318C6',
                          },
                          p: 0,
                          mr: 1.5
                        }}
                      />
                      <Typography sx={{ fontWeight: '500', fontSize: '15px', color: '#1E2939', fontFamily: 'var(--font-montserrat-medium)' }}>
                        Yes
                      </Typography>
                    </Box>

                    <Box
                      onClick={() => setGenerationParams(prev => ({
                        ...prev,
                        add_caption: false,
                        caption_text_1: '',
                        caption_text_2: '',
                        captions_data: []
                      }))}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        border: '1.5px solid #6318C6',
                        borderRadius: '12px',
                        py: '10px',
                        px: 2,
                        cursor: 'pointer',
                        backgroundColor: generationParams.add_caption === false ? '#F9F6FE' : 'transparent',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <Radio
                        checked={generationParams.add_caption === false}
                        onChange={() => setGenerationParams(prev => ({
                          ...prev,
                          add_caption: false,
                          caption_text_1: '',
                          caption_text_2: '',
                          captions_data: []
                        }))}
                        value="no"
                        name="add-caption-group"
                        sx={{
                          color: '#6318C6',
                          '&.Mui-checked': {
                            color: '#6318C6',
                          },
                          p: 0,
                          mr: 1.5
                        }}
                      />
                      <Typography sx={{ fontWeight: '500', fontSize: '15px', color: '#1E2939', fontFamily: 'var(--font-montserrat-medium)' }}>
                        No
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* 13. Select brand logo */}
                <Box>
                  {/* Brand Watermark Toggle - Commented out as requested */}
                  {/* 
                  <Box
                    onClick={() => {
                      const newValue = !generationParams.brand_watermark;
                      setGenerationParams(prev => ({ ...prev, brand_watermark: newValue }));
                      if (!newValue) {
                        setSelectedBrandLogoId('');
                      }
                      setFieldErrors(prev => ({ ...prev, brand_watermark: '' }));
                    }}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'row', sm: 'row', md: 'row' },
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: { xs: 1, sm: 2 },
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      padding: { xs: '0 12px', sm: '0 16px' },
                      backgroundColor: 'white',
                      width: '100%',
                      height: '45px',
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                      '&:hover': {
                        borderColor: '#9ca3af',
                        backgroundColor: '#f9fafb'
                      },
                      ...(generationParams.brand_watermark && {
                        borderColor: '#6318C6',
                        backgroundColor: '#f3f0ff'
                      })
                    }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flex: 1,
                      minWidth: 0
                    }}>
                      <input
                        type="checkbox"
                        checked={generationParams.brand_watermark}
                        onChange={(e) => {
                          e.stopPropagation();
                          const checked = e.target.checked;
                          setGenerationParams(prev => ({ ...prev, brand_watermark: checked }));
                          if (!checked) {
                            setSelectedBrandLogoId('');
                          }
                        }}
                        style={{
                          width: '18px',
                          height: '18px',
                          accentColor: '#6318C6',
                          marginRight: '8px',
                          pointerEvents: 'none',
                          flexShrink: 0
                        }}
                      />
                      <Typography sx={{
                        fontSize: { xs: '13px', sm: '14px', md: '15px' },
                        fontWeight: '500',
                        color: 'black',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                        fontFamily: 'var(--font-montserrat-medium)'
                      }}>
                        Add Brand Watermark
                      </Typography>
                    </Box>
                  </Box>
                  */}

                  {/* Brand Logo Selection Section */}
                  <Box sx={{ mt: 1 }}>
                    <FormLabel component="legend" className="ab-form-label" sx={{ mb: 1 }}>
                      Select Brand Logo
                    </FormLabel>

                    {/* Custom Dropdown Trigger */}
                    <Box
                      onClick={() => setBrandLogoOpen(prev => !prev)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: '14px 16px',
                        borderRadius: '10px',
                        border: brandLogoOpen ? '2px solid #6318C6' : '1.5px solid #d1d5db',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        height: '45px',
                        boxSizing: 'border-box',
                        mb: 1,
                        '&:hover': { borderColor: '#9ca3af' },
                      }}
                    >
                      {selectedBrandLogoId ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {brandLogos.find(l => l.id === parseInt(selectedBrandLogoId))?.brand_logo_path && (
                            <img
                              src={getImageUrl(brandLogos.find(l => l.id === parseInt(selectedBrandLogoId)).brand_logo_path)}
                              alt="Brand Logo"
                              style={{
                                width: '20px',
                                height: '20px',
                                objectFit: 'contain',
                                borderRadius: '4px',
                                border: '1px solid #E5E7EB',
                                backgroundColor: 'white'
                              }}
                            />
                          )}
                          <Typography sx={{ fontWeight: '500', fontSize: '11.08px', color: '#1E2939', fontFamily: 'var(--font-montserrat-medium)' }}>
                            {brandLogos.find(l => l.id === parseInt(selectedBrandLogoId))?.brand_name}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ color: '#9CA3AF', fontWeight: '500', fontSize: '15.15px', fontFamily: 'var(--font-montserrat-medium)' }}>
                          Select Brand Logo
                        </Typography>
                      )}
                      <KeyboardArrowDown
                        sx={{
                          color: '#6B7280',
                          fontSize: '22px',
                          transform: brandLogoOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                        }}
                      />
                    </Box>

                    {/* Custom Dropdown Options */}
                    {brandLogoOpen && (
                      <Paper
                        elevation={3}
                        sx={{
                          mt: 0.5,
                          maxHeight: '300px',
                          overflowY: 'auto',
                          borderRadius: '16px',
                          border: '1px solid #E5E7EB',
                          backgroundColor: 'white',
                          zIndex: 10,
                          position: 'relative' // Changed to relative to push content instead of overlapping if needed, or keep it consistent with bust color
                        }}
                      >
                        {brandLogos.length === 0 ? (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 2, fontFamily: 'var(--font-montserrat-medium)' }}>
                              No brand logos found.
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                // Save form state so it can be restored when user returns from Brand Logos.
                                const brandLogosDraft = {
                                  generationParams,
                                  activePhotoTab,
                                  captionsList,
                                  selectedBrandLogoId,
                                  uploadedFiles,
                                  uploadPreviews,
                                  allSelectedFiles,
                                  fileValidationStatus,
                                  customerFile,
                                  customerPreview,
                                  customerUploadError
                                };
                                setModelStudioDraft(brandLogosDraft);
                                const fullSnapshot = {
                                  generationParams,
                                  activePhotoTab,
                                  captionsList,
                                  selectedBrandLogoId
                                };
                                const minimalSnapshot = {
                                  generationParams: {
                                    category: generationParams.category,
                                    model_type: generationParams.model_type,
                                    output_format: generationParams.output_format,
                                    bust_color: generationParams.bust_color,
                                    ethnicity: generationParams.ethnicity,
                                    clothing_type: generationParams.clothing_type,
                                    bust_finish: generationParams.bust_finish,
                                    gender_option_id: generationParams.gender_option_id,
                                    watermark: generationParams.watermark,
                                    brand_watermark: generationParams.brand_watermark,
                                    media_quality_id: generationParams.media_quality_id,
                                    selected_ai_model: generationParams.selected_ai_model
                                  },
                                  activePhotoTab,
                                  selectedBrandLogoId
                                };
                                try {
                                  sessionStorage.setItem('ai_generation_restore_state', JSON.stringify(fullSnapshot));
                                } catch (e) {
                                  console.warn('sessionStorage quota exceeded, saving minimal state:', e);
                                  try {
                                    sessionStorage.setItem('ai_generation_restore_state', JSON.stringify(minimalSnapshot));
                                  } catch (e2) {
                                    console.error('sessionStorage unavailable, state will not be restored:', e2);
                                  }
                                }
                                try { localStorage.setItem(AI_MODEL_STUDIO_RETURN_KEY, '1'); } catch (_) { }
                                navigate('/profile/brands', {
                                  state: {
                                    returnToGenerate: true,
                                    aiGenerationDraft: brandLogosDraft
                                  }
                                });
                              }}
                              sx={{ textTransform: 'none', borderColor: '#6318C6', color: '#6318C6' }}
                            >
                              Go to Brand Logos
                            </Button>
                          </Box>
                        ) : (
                          brandLogos.map((logo) => {
                            const isSelected = selectedBrandLogoId === logo.id.toString() || selectedBrandLogoId === logo.id;
                            return (
                              <Box
                                key={logo.id}
                                onClick={() => {
                                  setSelectedBrandLogoId(logo.id.toString());
                                  setGenerationParams(prev => ({ ...prev, brand_watermark: true }));
                                  setBrandLogoOpen(false);
                                  setFieldErrors(prev => ({ ...prev, brand_watermark: '' }));
                                }}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  p: '12px 16px',
                                  gap: 2,
                                  borderBottom: '1px solid #F3F4F6',
                                  cursor: 'pointer',
                                  backgroundColor: isSelected ? '#F9F6FE' : 'transparent',
                                  '&:hover': {
                                    backgroundColor: '#F3F4F6'
                                  },
                                  '&:last-child': {
                                    borderBottom: 'none'
                                  }
                                }}
                              >
                                <Box
                                  sx={{
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '8px',
                                    border: '1px solid #E5E7EB',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    overflow: 'hidden',
                                    backgroundColor: 'white'
                                  }}
                                >
                                  {logo.brand_logo_path ? (
                                    <img
                                      src={getImageUrl(logo.brand_logo_path)}
                                      alt={logo.brand_name}
                                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                  ) : (
                                    <Typography variant="caption" sx={{ fontSize: '10px', color: '#9CA3AF' }}>LOGS</Typography>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                  <Typography sx={{ fontWeight: '600', fontSize: '15px', color: '#1F2937', fontFamily: 'var(--font-montserrat-medium)' }}>
                                    {logo.brand_name}
                                  </Typography>
                                  <Typography sx={{ fontSize: '12px', color: '#6B7280', fontFamily: 'var(--font-montserrat-medium)' }}>
                                    {logo.industry_type_name || 'Premium Jewelry'} {/* Using industry name or placeholder */}
                                  </Typography>
                                </Box>
                                {isSelected && <Check sx={{ color: '#6318C6', fontSize: '20px' }} />}
                              </Box>
                            );
                          })
                        )}
                      </Paper>
                    )}
                  </Box>
                  {fieldErrors.brand_watermark && (
                    <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: '14px', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500 }}>
                      {fieldErrors.brand_watermark}
                    </Typography>
                  )}
                </Box>







                {/* Generate Button */}
                {(() => {
                  const currentCredits = calculateCredits();
                  const isSuspended = isSubscriptionSuspended();
                  const hasInsufficient = hasInsufficientCredits();
                  const canGenerateNow = canGenerate();
                  // Keep a UI-level disabled flag (includes file-upload constraints outside canGenerate()).
                  const isGenerateDisabled = uploadedFiles.length === 0 || !canGenerateNow || (activePhotoTab === 'customer' && !customerFile);

                  return (
                    <StyledButton
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      disabled={isGenerateDisabled}
                      onClick={handleGenerate}
                      startIcon={<AutoAwesome />}
                      sx={{
                        height: '50px',
                        minHeight: '50px',
                        fontWeight: '600',
                        fontSize: (() => {
                          if (isSuspended || hasInsufficient || hasZeroCredits()) {
                            return { xs: '11px', sm: '12px', md: '17px' }; // Smaller for suspended/insufficient credits
                          }
                          return { xs: '14px', sm: '16px', md: '17px' }; // Larger for normal state
                        })(),
                        backgroundColor: isGenerateDisabled ? '#B88AE8' : '#651BC6',
                        color: '#fff !important',
                        '&.Mui-disabled': {
                          backgroundColor: '#B88AE8 !important',
                          color: '#fff !important',
                          opacity: 1
                        },
                        '&:hover': {
                          backgroundColor: isGenerateDisabled ? '#B88AE8' : '#651BC6'
                        }
                      }}
                    >
                      {uploadedFiles.length > 0 ?
                        (() => {
                          if (isSuspended || hasZeroCredits()) {
                            return getSubscriptionStatusMessage().title;
                          }
                          if (currentCredits === 0) {
                            return isVideoFormat() ? 'Generate Video' : 'Generate Image';
                          }
                          return isVideoFormat() ? `Generate Video (${currentCredits} ${FlutterServicesAPI.getCreditLabel().toLowerCase()})` : `Generate Image (${currentCredits} ${FlutterServicesAPI.getCreditLabel().toLowerCase()})`;
                        })() :
                        'Upload Image'
                      }
                    </StyledButton>
                  );
                })()}


              </Box>
            </Paper>
          </Grid>
        </Grid>

        {pricingError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {pricingError}
          </Alert>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {/* Timeout Warning - Show when there's a timeout but generation might still be processing */}
        {error && (error.includes('timeout') || error.includes('timed out')) && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <AlertTitle>Generation May Still Be Processing</AlertTitle>
            <Typography variant="body2">
              The request timed out, but your image generation may still be processing in the background.
              Please check the <strong>History tab</strong> for updates. The generation will appear there once completed.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/history')}
                startIcon={<History />}
              >
                Go to History Tab
              </Button>
            </Box>
          </Alert>
        )}

        <AutoCloseAlert
          severity="success"
          message={successMessage}
          show={!!successMessage}
          onClose={() => setSuccessMessage('')}
          autoCloseDelay={4000}
        />
      </Box >

      {/* Feedback Dialog */}
      {
        feedbackDialog && (
          <Dialog
            open={feedbackDialog}
            onClose={() => setFeedbackDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6" fontWeight={600}>
                  Submit Feedback
                </Typography>
                <IconButton onClick={() => setFeedbackDialog(false)}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <TextField
                label="Comments"
                multiline
                rows={4}
                fullWidth
                value={feedback.comments}
                onChange={(e) => setFeedback({ ...feedback, comments: e.target.value })}
                margin="normal"
              />
              <FormControl component="fieldset" sx={{ mt: 2 }}>
                <FormLabel component="legend" className="ab-form-label">Satisfaction</FormLabel>
                <RadioGroup
                  row
                  value={feedback.satisfactory}
                  onChange={(e) => setFeedback({ ...feedback, satisfactory: e.target.value })}
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                  <FormControlLabel value="Neutral" control={<Radio />} label="Neutral" />
                </RadioGroup>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setFeedbackDialog(false)} color="primary">
                Cancel
              </Button>
              <Button onClick={handleFeedbackSubmit} color="primary" autoFocus>
                Submit
              </Button>
            </DialogActions>
          </Dialog>
        )
      }

      {/* Batch Status Dialog */}
      {
        batchStatusDialog && (
          <Dialog
            open={batchStatusDialog}
            onClose={() => setBatchStatusDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6" fontWeight={600}>
                  Batch Processing Status
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={`${batchProgress.completed}/${batchProgress.total} Complete`}
                    color={batchProgress.completed === batchProgress.total ? "success" : "warning"}
                    variant="outlined"
                  />
                  {batchProgress.batchId && (
                    <Chip
                      label={`Batch: ${batchProgress.batchId.substring(0, 8)}...`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Overall Progress
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round((batchProgress.completed / batchProgress.total) * 100)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(batchProgress.completed / batchProgress.total) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Individual File Status
              </Typography>

              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {Object.entries(batchProgress.statuses).map(([generationId, fileInfo]) => (
                  <ListItem key={generationId} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {fileInfo.status === 'COMPLETED' && (
                        <CheckCircle color="success" />
                      )}
                      {fileInfo.status === 'PROCESSING' && (
                        <PlayArrow color="primary" />
                      )}
                      {fileInfo.status === 'PENDING' && (
                        <Schedule color="warning" />
                      )}
                      {fileInfo.status === 'FAILED' && (
                        <Error color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={fileInfo.fileName}
                      secondary={
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <Chip
                            label={fileInfo.status}
                            size="small"
                            color={
                              fileInfo.status === 'COMPLETED' ? 'success' :
                                fileInfo.status === 'PROCESSING' ? 'primary' :
                                  fileInfo.status === 'PENDING' ? 'warning' :
                                    fileInfo.status === 'FAILED' ? 'error' : 'default'
                            }
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            ID: {generationId}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setBatchStatusDialog(false)}
                variant="outlined"
              >
                Close
              </Button>
              {batchProgress.completed === batchProgress.total && (
                <Button
                  onClick={() => {
                    setBatchStatusDialog(false);
                    navigate('/history');
                  }}
                  variant="contained"
                  color="primary"
                  startIcon={<Download />}
                >
                  View in History
                </Button>
              )}
            </DialogActions>
          </Dialog>
        )
      }

      {/* Processing Status */}
      {
        processingStatus && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1300,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderBottom: '1px solid #e5e7eb',
              p: 2,
            }}
          >
            <Container maxWidth="lg">
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={2}>
                  <CircularProgress size={24} />
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {processingStatus.status?.display_name || 'Processing...'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Generation ID: {processingStatus.id}
                      {batchProgress.total > 1 && (
                        <span> • {batchProgress.completed}/{batchProgress.total} files complete</span>
                      )}
                      {processingStatus.batchId && (
                        <span> • Batch: {processingStatus.batchId.substring(0, 8)}...</span>
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" gap={1}>
                  {batchProgress.total > 1 && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setBatchStatusDialog(true)}
                      startIcon={<FilterList />}
                    >
                      View Batch Status
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setPollingCancelled(true);
                      if (pollingTimeout) {
                        clearTimeout(pollingTimeout);
                      }
                      setProcessingStatus(null);
                    }}
                    startIcon={<Cancel />}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
              {batchProgress.total > 1 && (
                <Box mt={2}>
                  {/* Overall Batch Progress */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="caption" color="text.secondary">
                      Batch Progress
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {Math.round((batchProgress.completed / batchProgress.total) * 100)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(batchProgress.completed / batchProgress.total) * 100}
                    sx={{ height: 4, borderRadius: 2, mb: 2 }}
                  />


                </Box>
              )}
            </Container>
          </Box>
        )
      }
      {/* Guidelines Dialog */}
      <GuidelinesDialog />
      {/* Generation Preview Dialog */}
      <GenerationPreviewDialog
        open={showPreviewDialog}
        onClose={() => {
          setShowPreviewDialog(false);
          // When closing the preview, redirect to history page as requested
          navigate('/history');
        }}
        generation={completionGeneration}
      />

      {/* Caption Selection Dialog */}
      <Dialog
        open={isCaptionPopupOpen}
        onClose={() => setIsCaptionPopupOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '25px',
            maxWidth: '550px',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '95vh',
            width: { xs: 'calc(100% - 0px)', sm: '420px' },
            mx: { xs: 'auto', sm: 'auto' }
          }
        }}
      >
        <Box
          sx={{
            p: '15px 20px',
            borderBottom: '1.2px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'white',
            zIndex: 10
          }}
        >
          <Typography sx={{
            fontSize: '16.69px',
            fontFamily: 'var(--font-montserrat-semibold) !important',
            fontWeight: 600,
            color: '#000000'
          }}>
            Add Caption
          </Typography>
          <IconButton onClick={() => setIsCaptionPopupOpen(false)} sx={{ p: 0 }}>
            <img src="/assets/img/icons/button_cl.svg" alt="close" style={{ width: 30, height: 30 }} />
          </IconButton>
        </Box>

        <Box
          ref={captionsContainerRef}
          sx={{
            p: { xs: 2, md: 3 },
            overflowY: 'auto',
            flex: 1,
            bgcolor: 'white',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5
          }}
        >
          {captionsList.map((item, index) => (
            <Box key={item.id} sx={{
              bgcolor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '17.17px',
              p: '12px 20px 7px 20px',
              position: 'relative'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '15px', fontFamily: 'var(--font-montserrat-semibold) !important', color: '#000000' }}>
                  Caption {index + 1}
                </Typography>
              </Box>

              <Typography sx={{ fontSize: '14px', fontWeight: 600, fontFamily: 'var(--font-montserrat-semibold) !important', color: '#000000', mb: 1 }}>
                Uploaded Image
              </Typography>

              <Box
                sx={{
                  border: '1.5px dashed #D1D5DC',
                  borderRadius: '15.03px',
                  bgcolor: '#F9FAFB',
                  height: '110px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  mb: 2,
                  overflow: 'hidden',
                  position: 'relative',
                  '&:hover': {
                    borderColor: '#C3A5E4'
                  }
                }}
              >
                {item.preview ? (
                  <img src={item.preview} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <>
                    <img src="/assets/img/icons/upload _are.svg" alt="uploaded" style={{ width: 32, height: 32, marginBottom: 8 }} />
                    <Typography sx={{ color: '#6B7280', fontSize: '12px', fontFamily: 'var(--font-montserrat-medium) !important' }}>
                      No uploaded image available
                    </Typography>
                  </>
                )}
              </Box>

              <Typography sx={{ fontSize: '14px', fontWeight: 600, fontFamily: 'var(--font-montserrat-semibold) !important', color: '#000000', mb: 1 }}>
                Caption 1
              </Typography>
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Enter caption 1 (max 30 characters)"
                  autoFocus={index === captionsList.length - 1}
                  value={item.text_1 || item.text || ''}
                  onChange={(e) => handleCaptionTextChange(item.id, 'text_1', e.target.value)}
                  inputProps={{
                    maxLength: 30,
                    style: {
                      fontSize: '20px',
                      fontFamily: 'var(--font-freeserif)',
                      color: '#4B5563'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: '15.03px',
                      padding: '12px 14px',

                      '& fieldset': { borderColor: '#E5E7EB', borderWidth: '1px' },
                      '&:hover fieldset': { borderColor: '#D1D5DC' },
                      '&.Mui-focused fieldset': { borderColor: '#6318C6' },

                      // ✅ placeholder style
                      '& textarea::placeholder': {
                        fontSize: '13px', // smaller size
                        color: '#9CA3AF', // optional
                        opacity: 1 // needed for some browsers
                      }
                    }
                  }}
                />
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, color: '#9CA3AF', fontFamily: 'var(--font-montserrat-medium)' }}>
                  {(item.text_1 || item.text || '').length} / 30
                </Typography>
              </Box>

              <Typography sx={{ fontSize: '14px', fontWeight: 600, fontFamily: 'var(--font-montserrat-semibold) !important', color: '#000000', mb: 1 }}>
                Caption 2
              </Typography>
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Enter caption 2 (max 30 characters)"
                  value={item.text_2 || ''}
                  onChange={(e) => handleCaptionTextChange(item.id, 'text_2', e.target.value)}
                  inputProps={{
                    maxLength: 30,
                    style: {
                      fontSize: '17px',
                      fontFamily: 'var(--font-freesans)',
                      color: '#4B5563'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: '15.03px',
                      padding: '12px 14px',
                      '& fieldset': { borderColor: '#E5E7EB', borderWidth: '1px' },
                      '&:hover fieldset': { borderColor: '#D1D5DC' },
                      '&.Mui-focused fieldset': { borderColor: '#6318C6' },
                      '& textarea::placeholder': {
                        fontSize: '13px',
                        color: '#9CA3AF',
                        opacity: 1
                      }
                    }
                  }}
                />
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, color: '#9CA3AF', fontFamily: 'var(--font-montserrat-medium)' }}>
                  {(item.text_2 || '').length} / 30
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{
          p: { xs: 2.5, md: 3 },
          bgcolor: 'white',
          borderTop: '1.2px solid #E5E7EB',
          boxShadow: '0px -3.82px 11.45px 0px rgba(0, 0, 0, 0.05)',
          zIndex: 10
        }}>
          <Button
            variant="contained"
            fullWidth
            onClick={submitCaptions}
            sx={{
              background: 'linear-gradient(90deg, #9C52DB 0%, #EAA24A 100%)',
              boxShadow: 'none',
              color: 'white',
              textTransform: 'none',
              borderRadius: '10px !important',
              py: 1.6,
              height: '50px !important',
              fontSize: '16px',
              fontWeight: 600,
              fontFamily: 'var(--font-montserrat-semibold) !important',
              '&:hover': {
                background: 'linear-gradient(90deg, #EAA24A 0%, #9C52DB 100%)',
                opacity: 0.9,
                boxShadow: 'none'
              }
            }}
          >
            Submit
          </Button>
        </Box>
      </Dialog>

    </div>
  );
};

export default AIGenerationWizard;
